import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
// Standalone, não CommonModule: só a formatação de data do histórico e o avatar do médico, que o
// card e os dois diálogos repetem a partir do mesmo ng-template.
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { ImageModule } from 'primeng/image';
// import type: só o TIPO do popup, para `alternarMenuDoCard` ler o estado real dele. O módulo já
// entra pelo `imports` do componente; o tipo não carrega nada a mais em runtime.
import type { Menu } from 'primeng/menu';
import { AvatarModule } from 'primeng/avatar';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { VisualizadorEmail } from '@/features/servicos/shared/visualizador-email/visualizador-email';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { TimelineModule } from 'primeng/timeline';

import { BiometriaService } from '@/core/api/sicom-api/registro/medico/biometria/biometria.service';
import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { PermissionService } from '@/core/permissions/permission.service';
import type { HistoricoAndamento, KanbanCriticidade, KanbanSolicitacao, KanbanStatus, Responsavel } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';
import { LoggerService } from '@/core/services/logger.service';
import { ConclusoesNoPortalService, chaveDaConclusao } from '@/features/servicos/shared/conclusoes-no-portal/conclusoes-no-portal.service';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import { mapearItemStatusProtocolo } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.mapper';
import { chaveDeNome, paraEventosDeTimeline } from '@/features/servicos/shared/kanban-protocolos/kanban-historico.presenter';
import { iniciaisDoNome } from '@/features/servicos/shared/avatar-responsavel/avatar-responsavel.presenter';
import { FotosResponsaveisService } from '@/features/servicos/shared/avatar-responsavel/fotos-responsaveis.service';
import { indiceDeCorDoStatus } from '@/features/servicos/shared/status-kanban/status-kanban.presenter';
import { DIAS_PARA_EX_OFFICIO, ECampoFiltro, type FiltroDaColuna, camposDaColuna, cardAtendeFiltro, filtroAtivo } from '@/features/servicos/shared/kanban-protocolos/kanban-filtros.presenter';
import { efetivacaoSeraExOfficio, mensagemConfirmacaoExOfficio, servicoTemExOfficio } from '@/features/servicos/shared/utils/ex-officio.presenter';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
// import type: ItemStatus é usado só como tipo (interface) neste arquivo, nunca em runtime,
// então o import não precisa carregar o módulo de fato.
import { type TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import type { ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { AoAparecerDirective } from '@/shared/directives/ao-aparecer/ao-aparecer.directive';
import { MascaraUtil } from '@/shared/utils/mascara-util';
import { separarCrmUf } from '@/features/servicos/shared/utils/crm.utils';
import type { AcaoServico, CampoConfirmacao } from '@/features/servicos/shared/utils/servicos-acoes.utils';
import {
    acoesOcultasPorUf,
    campoDaConfirmacao,
    chaveDaDataDeConfirmacao,
    crmUfDoRostoDoCard,
    dataDeConfirmacaoParaPayload,
    desfechoDaAcao,
    efetivacaoSoNaTelaDeDetalhe,
    identificacaoPorCnpj,
    rolesDoServico,
    rostoDoCardEhDoDiretorTecnico,
    rotuloDoCampoDeConfirmacao
} from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { formatarDataTramitacao, identificadorProtocolo as identificadorProtocoloUtil, modalidadeInscricaoRotulo, situacaoClasse, situacaoRotulo } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/** Coluna do quadro com os itens (já combinados com o status rastreado) que caem nela. */
export interface KanbanColuna {
    status: KanbanStatus;
    /** Cards que o filtro da coluna deixou passar. */
    itens: ItemStatus[];
    /**
     * Cards da coluna antes do filtro.
     *
     * O contador mostra os dois quando há filtro: sem isso, filtro ligado e esquecido se lê como
     * "sumiu trabalho do quadro" — e é o defeito mais provável de uma tela com filtro por área.
     */
    total: number;
}

/** Movimento em confirmação no diálogo: item, coluna de origem e coluna de destino. */
interface MovimentoPendente {
    item: ItemStatus;
    origem: KanbanStatus;
    destino: KanbanStatus;
}

function inteiro(valor?: string): number | null {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return null;
    }
    const numero = Number(texto);
    return Number.isInteger(numero) ? numero : null;
}

/** Chave canônica ano|numero. Sempre a partir de inteiros, para não divergir por zero à esquerda, espaço etc. */
function chaveNumeroAno(ano: number | null, numero: number | null): string {
    return `${ano ?? ''}|${numero ?? ''}`;
}

/** Ano+numero identificam o protocolo sozinhos: são únicos globalmente, independente de UF. */
export function chaveProtocolo(item: ItemStatus): string {
    return chaveNumeroAno(inteiro(item.anoProtocolo), inteiro(item.numeroProtocolo));
}

/**
 * Por que a efetivação está inerte. Vive numa constante porque agora é o `title` de um item de
 * menu, e não mais o `title` de um botão visível: dentro do menu o texto é a única explicação que
 * sobra, já que não há mais o botão apagado ao lado para o analista comparar.
 */
const MOTIVO_SEM_PAGAMENTO = 'Protocolo sem pagamento confirmado: não pode ser efetivado.';

/** Quadro de andamento interno dos protocolos de um serviço. Não altera a situação no portal. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-kanban-protocolos',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CdkDropListGroup,
        CdkDropList,
        CdkDrag,
        DialogModule,
        ButtonModule,
        MenuModule,
        ImageModule,
        TextareaModule,
        SelectModule,
        TimelineModule,
        AvatarModule,
        CheckboxModule,
        InputNumberModule,
        FormsModule,
        NgTemplateOutlet,
        AoAparecerDirective,
        VisualizadorEmail
    ],
    templateUrl: './kanban-protocolos.html',
    styleUrl: './kanban-protocolos.scss'
})
export class KanbanProtocolos {
    private readonly kanbanServicosService = inject(KanbanServicosService);
    /** Cache de fotos por id de usuário, compartilhado com o avatar dos relatórios. */
    private readonly fotos = inject(FotosResponsaveisService);
    private readonly biometriaService = inject(BiometriaService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly logger = inject(LoggerService);
    private readonly permissionService = inject(PermissionService);
    // Injeção hierárquica: o facade é `providers` do dashboard-servicos, o mesmo que alimenta a lista
    // deste quadro. Pedi-lo aqui usa a instância do serviço aberto, não uma cópia com outro estado.
    private readonly facade = inject(DashboardServicosFacade);
    private readonly ufEfetivaStore = inject(UfEfetivaStore);

    /** Espelho do store para o template: perfil nacional acompanha o quadro, não escreve nele. */
    readonly somenteLeitura = computed(() => this.ufEfetivaStore.somenteLeitura());

    /** Itens do serviço (mesma fonte da lista/tabela) a distribuir nas colunas do quadro. */
    readonly itens = input<ItemStatus[]>([]);
    /** Serviço selecionado; dispara o carregamento do quadro junto com `uf`. */
    readonly idServico = input<number | undefined>(undefined);
    /** UF do quadro (a consulta de solicitações rastreadas é por UF). */
    readonly uf = input('');
    /** Emitido quando o usuário abre um card para ver o detalhe do protocolo. */
    readonly itemSelected = output<ItemStatus>();
    /**
     * Movimento gravado numa coluna de pendencia COM o aviso marcado. Quem avisa e a tela de
     * detalhe: o aviso ao medico e um e-mail que o analista revisa antes de enviar, e o compositor
     * (com template e destinatarios) vive la. O quadro so diz que o aviso foi pedido.
     */
    readonly avisoPendenciaSolicitado = output<ItemStatus>();
    /**
     * Pedido de encerrar em lote os cards selecionados nesta coluna. O quadro não orquestra nada —
     * quem decide o que fazer com os itens (efetivar ou cancelar, um por vez, numa fila sequencial) é
     * o diálogo.
     *
     * `colunaDestino` viaja junto porque o quadro é o ÚNICO lugar que sabe qual coluna é a conclusiva
     * (`colunaDeConclusao`, derivada do `conclusivo` que só o carregamento de status traz). O diálogo
     * mora no `dashboard-servicos`, que não carrega status nenhum: sem este campo, quem dispara a fila
     * teria de adivinhar o destino do movimento — ou consultar os status de novo, criando uma segunda
     * verdade sobre qual é a coluna de conclusão.
     */
    readonly encerrarEmLoteSolicitado = output<{ itens: ItemStatus[]; coluna: KanbanColuna; colunaDestino: KanbanColuna }>();
    /**
     * Natureza do solicitante do quadro. É o eixo de que dependem as duas decisões sobre o rosto do
     * card — se ele aparece e DE QUEM ele é: na pessoa física é o próprio titular; na jurídica, o
     * diretor técnico, porque o sujeito do protocolo é a empresa mas quem está sob análise é o médico
     * responsável por ela.
     *
     * Substituiu dois booleanos (`exibirDiretorTecnico`/`exibirFotoMedico`) que o chamador derivava
     * deste mesmo valor: separados, um podia dizer "pessoa jurídica" e o outro não, e o card
     * mostrava o rosto de alguém sob o nome de outro.
     */
    readonly tipoPessoa = input<TipoServicoPessoa>('pessoa-fisica');
    /**
     * Achado da revisão final: a anuidade pendente do card não gate mais neste input — ela
     * aparece sempre que `isAnuidadePaga` é false, como no card antigo. Nada mais neste
     * componente lê este valor; mantido só para não quebrar quem ainda o passa (quadro/dashboard).
     */
    readonly exibirFiltroAnuidade = input(false);
    /** Código do serviço do quadro. O gate de permissão é por código, não pelo id numérico. */
    readonly codigoServico = input('');

    /** A linha do diretor técnico só existe onde ele existe: nos protocolos de pessoa jurídica. */
    protected readonly exibirDiretorTecnico = computed(() => this.tipoPessoa() === 'pessoa-juridica');

    /**
     * O histórico no formato que a timeline desenha. `computed`, e não um segundo signal: derivar de
     * `historico()` mantém um estado só — dois signals sincronizados à mão divergem no primeiro
     * caminho de erro que esquecer de atualizar o segundo.
     */
    protected readonly eventosHistorico = computed(() => paraEventosDeTimeline(this.historico()));

    /** Rótulo/classe da situação do protocolo no portal, e rótulo da modalidade — mesma tradução do painel de detalhe (dashboard-servicos). */
    protected readonly situacaoRotulo = situacaoRotulo;
    protected readonly situacaoClasse = situacaoClasse;
    protected readonly modalidadeInscricaoRotulo = modalidadeInscricaoRotulo;

    readonly status = signal<KanbanStatus[]>([]);
    readonly solicitacoes = signal<Map<string, KanbanSolicitacao>>(new Map());
    readonly responsaveis = signal<Responsavel[]>([]);
    readonly criticidades = signal<KanbanCriticidade[]>([]);
    readonly responsavelSelecionado = signal<number | null>(null);
    readonly criticidadeSelecionada = signal<number | null>(null);
    readonly carregando = signal(false);
    readonly erro = signal<string | null>(null);
    readonly movimentoPendente = signal<MovimentoPendente | null>(null);
    /** Marcado no diálogo: ao gravar o movimento, o médico será avisado da pendência. */
    readonly avisarMedico = signal(false);

    /** Filtro aplicado em cada coluna, pelo id dela. Coluna ausente do mapa não filtra nada. */
    readonly filtrosPorColuna = signal<Map<number, FiltroDaColuna>>(new Map());
    /** Coluna com o painel de filtro aberto; `null` mantém o diálogo fechado. */
    readonly colunaEmFiltro = signal<KanbanStatus | null>(null);
    /**
     * Edição em curso no painel.
     *
     * Rascunho, e não o filtro aplicado: o quadro fica atrás do diálogo, então aplicar a cada tecla
     * mudaria uma tela que o analista não está vendo. O commit é no "Aplicar".
     */
    readonly rascunhoFiltro = signal<FiltroDaColuna>({});

    protected readonly ECampoFiltro = ECampoFiltro;

    /**
     * Opções fixas do painel de filtro.
     *
     * Rótulos ficam aqui, e não no presenter: o presenter decide REGRA (quem passa no filtro), a
     * tela decide como a pergunta é escrita. Os códigos são os mesmos que `situacaoRotulo` traduz.
     */
    protected readonly opcoesSituacaoFiltro = [
        { rotulo: 'Aberto', valor: 'A' },
        { rotulo: 'Tramitado', valor: 'T' },
        { rotulo: 'Pendência', valor: 'P' }
    ];
    protected readonly opcoesModalidadeFiltro = [
        { rotulo: 'Cadastro', valor: 'C' },
        { rotulo: 'Registro', valor: 'R' }
    ];
    protected readonly opcoesPagamentoFiltro = [
        { rotulo: 'Em dia', valor: 'em-dia' },
        { rotulo: 'Com pendência', valor: 'pendente' }
    ];
    protected readonly opcoesIndicacaoFiltro = [
        { rotulo: 'Aceitou', valor: 'ACEITOU' },
        { rotulo: 'Recusou', valor: 'RECUSOU' },
        { rotulo: 'Aguardando', valor: 'AGUARDANDO' },
        { rotulo: 'Não sócio', valor: 'NAO_SOCIO' }
    ];
    protected readonly opcoesAnotacaoFiltro = [
        { rotulo: 'Com anotação', valor: 'com' },
        { rotulo: 'Sem anotação', valor: 'sem' }
    ];
    protected readonly opcoesAvisoFiltro = [
        { rotulo: 'Já avisado', valor: 'avisado' },
        { rotulo: 'Aguardando aviso', valor: 'sem-aviso' }
    ];
    protected readonly opcoesDesfechoFiltro = [
        { rotulo: 'Efetivado', valor: 'efetivado' },
        { rotulo: 'Cancelado', valor: 'cancelado' }
    ];

    /** O prazo vem do presenter para o rótulo nunca divergir da regra que filtra de fato. */
    protected readonly opcoesExOfficioFiltro = [{ rotulo: `Aptas (${DIAS_PARA_EX_OFFICIO}+ dias e sem quitar)`, valor: true }];

    /**
     * A inscrição ex officio existe só para o pré-prestador: é lá que o Conselho inscreve a empresa
     * por decurso de prazo. Oferecer o filtro nos demais serviços devolveria sempre lista vazia.
     */
    protected readonly ofereceExOfficio = computed(() => servicoTemExOfficio(this.codigoServico()));

    /** Campos que a coluna aberta oferece — decididos pelas flags dela, nunca pelo nome. */
    readonly camposDoFiltro = computed<ECampoFiltro[]>(() => {
        const coluna = this.colunaEmFiltro();
        return coluna ? camposDaColuna(coluna, this.exibirDiretorTecnico(), this.ofereceExOfficio()) : [];
    });

    /**
     * E-mail aberto a partir do histórico. `null` mantém o diálogo fechado.
     *
     * O HTML não vem junto do histórico: um template passa de dezenas de KB, e a linha do tempo
     * inteira viria carregada deles só para desenhar um botão. Ele é buscado no clique.
     */
    readonly emailAberto = signal<string | null>(null);
    readonly carregandoEmail = signal(false);
    readonly anotacao = signal('');
    readonly salvando = signal(false);
    /** O que fazer com a solicitação no portal ao concluir; `null` enquanto o analista não escolhe. */
    readonly acaoAoConcluir = signal<AcaoServico | null>(null);
    /** Motivo do cancelamento, obrigatório quando a escolha é cancelar. */
    readonly motivoDaConclusao = signal('');
    /** Data (ISO, do input) ou observação exigida pela efetivação, conforme o serviço. */
    readonly dadoDaEfetivacao = signal('');
    /**
     * O que o portal JÁ aceitou, POR PROTOCOLO. Existe porque a finalização no portal é irreversível e
     * a gravação do movimento pode falhar depois dela: repetir o movimento não pode significar uma
     * SEGUNDA finalização sobre o mesmo protocolo — o que falta é só gravar.
     *
     * Por protocolo, e não pelo movimento pendente: a memória tem de sobreviver ao fechamento do
     * diálogo. Nesse estado `salvando` é `false`, então o X, o ESC e o Cancelar estão todos ativos —
     * e amarrada ao diálogo, a memória morria no fechamento, o card voltava a convidar um arraste e a
     * segunda finalização acontecia, com o erro falso "A solicitação não foi finalizada no portal".
     *
     * <p>Correção final (Crítico 4): era um `signal<Map<...>>` DESTE componente, alimentado só pelo
     * fluxo unitário. O encerramento em lote mantinha a mesma memória por conta própria, dentro da
     * fila, e as duas nunca se falavam: um protocolo que ficasse preso no lote (portal aceitou,
     * `mover` falhou) voltava ao quadro sem marca nenhuma e podia ser efetivado de novo pelo menu do
     * card. A memória passou a ser de um serviço só — ver `ConclusoesNoPortalService`, que também
     * explica por que o dono do fato não pode ser uma tela.</p>
     */
    private readonly conclusoesNoPortal = inject(ConclusoesNoPortalService);
    /**
     * `portal` do card cujo popup do kebab está aberto; `null` com todos fechados. Serve só ao
     * `aria-expanded`: o botão precisa declarar se o menu que ele controla está aberto agora.
     */
    readonly menuAbertoDoCard = signal<string | null>(null);
    /** Protocolo cujo histórico está aberto no diálogo; null quando fechado. */
    readonly historicoAberto = signal<ItemStatus | null>(null);
    readonly historico = signal<HistoricoAndamento[]>([]);
    readonly carregandoHistorico = signal(false);
    /** Diálogo de escrita do andamento, aberto por cima do histórico. */
    readonly escrevendoAndamento = signal(false);
    /** Texto do andamento sendo escrito. */
    readonly novoAndamento = signal('');
    /** Para quem encaminhar junto com o andamento. `null` mantém o dono atual. */
    readonly encaminharPara = signal<number | null>(null);
    readonly salvandoAndamento = signal(false);

    /**
     * O protocolo aberto no histórico já é rastreado no quadro.
     *
     * Anotar exige a solicitação gravada: o backend recusa anotar um protocolo que nunca foi movido,
     * para não criar registro de controle sem movimento nenhum — o histórico abriria com uma
     * observação antes da própria entrada no fluxo. Aqui isso vira campo desabilitado com explicação,
     * em vez de erro depois de o analista digitar.
     */
    readonly protocoloDoHistoricoRastreado = computed(() => {
        const item = this.historicoAberto();
        return !!item && this.solicitacoes().has(chaveProtocolo(item));
    });

    /**
     * Correção final (Correção 3): soma `somenteLeitura()` — o perfil nacional acompanha o histórico
     * (`abrirHistorico` fica liberado) mas não escreve nele. Antes desta correção só o `@if` do
     * template (`kanban-protocolos.html:624`) escondia o botão "Adicionar andamento"; no código, este
     * computed (e `abrirNovoAndamento`, que o antecede) ficavam mudos sobre o modo leitura — uma
     * chamada direta (atalho, teste, código futuro) a `inserirAndamento` passava por aqui sem barreira
     * nenhuma, porque é exatamente este computed que `inserirAndamento` consulta antes de gravar.
     */
    readonly podeInserirAndamento = computed(() => !this.ufEfetivaStore.somenteLeitura() && this.protocoloDoHistoricoRastreado() && this.novoAndamento().trim().length > 0 && !this.salvandoAndamento());
    /**
     * Protocolos concluídos nos últimos 30 dias, já mapeados para card. Vêm da tabela de controle do
     * kanban (endpoint próprio), e não da listagem de protocolos do portal — ver `itensDoQuadro`.
     */
    readonly concluidos = signal<ItemStatus[]>([]);
    /** Foto do médico já pronta para `src`, por chave `crm-uf`. Só contém quem tem foto. */
    readonly fotosMedicos = signal<Map<string, string>>(new Map());
    /**
     * Chaves já consultadas, com ou sem foto no retorno. Fora do sinal de propósito: serve para não
     * repetir a chamada, não para desenhar — e médico sem foto precisa ficar registrado aqui, senão
     * cada reaparição do card refaria a consulta que já se sabe vazia.
     */
    private readonly medicosConsultados = new Set<string>();

    /**
     * Cards marcados para o encerramento em lote, por chave de protocolo.
     *
     * Indexado por `chaveProtocolo` (ano|numero), e não por `item.portal`, que só serve ao `track`
     * do template: o portal identifica a linha desenhada, a chave identifica o protocolo — e é o
     * protocolo que vai para a fila.
     */
    private readonly selecionadosPorChave = signal<Record<string, ItemStatus>>({});
    readonly selecionados = this.selecionadosPorChave.asReadonly();
    readonly totalSelecionados = computed(() => Object.keys(this.selecionadosPorChave()).length);

    /**
     * Coluna dona da seleção: marcar card de outra coluna zera a anterior, porque colunas diferentes
     * têm destinos permitidos diferentes.
     */
    private readonly colunaDaSelecaoId = signal<number | null>(null);
    readonly colunaDaSelecao = this.colunaDaSelecaoId.asReadonly();

    /** O card está marcado para o encerramento em lote, pela chave do protocolo. */
    isSelecionado(item: ItemStatus): boolean {
        return chaveProtocolo(item) in this.selecionadosPorChave();
    }

    /**
     * Marca/desmarca o card para o lote. Selecionar um card de OUTRA coluna zera a seleção anterior:
     * colunas diferentes têm destinos permitidos diferentes, e o lote não pode misturar protocolos
     * que respondem a regras de destino distintas.
     */
    toggleSelecionado(item: ItemStatus, coluna: KanbanColuna): void {
        // Sem número/ano o protocolo não tem identidade no portal: não há o que finalizar.
        if (!this.arrastavel(item)) {
            return;
        }
        // Coluna de onde nenhum lote pode nascer não acumula seleção. O template já não desenha o
        // checkbox ali, mas este método é público: sem a guarda, uma seleção inalcançável pela tela
        // ainda poderia ser criada por outra porta e ficar presa, sem botão nenhum para consumi-la.
        if (!this.colunaPodeIniciarLote(coluna)) {
            return;
        }
        const chave = chaveProtocolo(item);
        const trocouDeColuna = this.colunaDaSelecaoId() !== null && this.colunaDaSelecaoId() !== coluna.status.id;
        const atuais = trocouDeColuna ? {} : { ...this.selecionadosPorChave() };

        if (chave in atuais) {
            delete atuais[chave];
        } else {
            atuais[chave] = item;
        }

        this.selecionadosPorChave.set(atuais);
        this.colunaDaSelecaoId.set(Object.keys(atuais).length ? coluna.status.id : null);
    }

    /**
     * Esvazia a seleção do lote. Chamado sempre que o contexto muda (recarga do quadro, troca de
     * serviço/UF, filtro aplicado): sem isso a seleção continuaria apontando para cards que a tela
     * não mostra mais.
     */
    limparSelecionados(): void {
        this.selecionadosPorChave.set({});
        this.colunaDaSelecaoId.set(null);
    }

    /**
     * Remove um único protocolo da seleção do lote, sem afetar os demais.
     *
     * O card continua arrastável de propósito, e nada impede mover um card selecionado pelo fluxo
     * UNITÁRIO (arraste ou menu) enquanto ele está marcado para o lote. Limpar a seleção INTEIRA
     * nesse caso puniria trabalho ainda válido — o analista que marcou cinco e moveu um mantém os
     * outros quatro. Zera a coluna dona só se a seleção ficar vazia: com pelo menos um selecionado
     * restante, a coluna dona continua sendo a mesma.
     */
    private removerDaSelecao(chave: string): void {
        if (!(chave in this.selecionadosPorChave())) {
            return;
        }
        const atuais = { ...this.selecionadosPorChave() };
        delete atuais[chave];
        this.selecionadosPorChave.set(atuais);
        if (!Object.keys(atuais).length) {
            this.colunaDaSelecaoId.set(null);
        }
    }

    /**
     * Um lote pode NASCER desta coluna? Só a posição dela no fluxo — não olha os cards.
     *
     * Duas condições: a coluna precisa ser NÃO conclusiva (de lá não há o que finalizar) e a
     * conclusiva precisa ser destino aceito pela MESMA regra do arraste (`destinoPermitido`) — da
     * primeira coluna, por exemplo, só se vai à seguinte, nunca direto à conclusiva.
     *
     * Separado de `podeEncerrarEmLote` porque governa o CHECKBOX, que é por card: o botão pergunta
     * "há o que encerrar aqui?", e o checkbox, "esta coluna pode originar um lote?". Enquanto os dois
     * eram a mesma pergunta, o checkbox aparecia em colunas de onde o botão nunca apareceria — o
     * analista marcava cards no backlog e a marcação não levava a lugar nenhum.
     *
     * Checagem própria do perfil nacional, e não herdada de `arrastavel`: esta pergunta é por
     * COLUNA, sem olhar item nenhum, então não há card cujo `arrastavel` carregar a resposta — sem
     * isto o checkbox continuaria desenhado (só desabilitado) em cada card da coluna.
     */
    colunaPodeIniciarLote(coluna: KanbanColuna): boolean {
        if (this.ufEfetivaStore.somenteLeitura()) {
            return false;
        }
        const conclusiva = this.colunaDeConclusao();
        if (!conclusiva || coluna.status.conclusivo) {
            return false;
        }
        // Mesma regra que o cdkDropListEnterPredicate aplica no arraste. Nem o botão nem o checkbox
        // passam por predicate nenhum: sem isto, ofereceriam pelo clique o que o arraste recusa.
        return this.destinoPermitido(coluna.status, conclusiva);
    }

    /**
     * Encerrar em lote a partir desta coluna faz sentido?
     *
     * A posição da coluna (`colunaPodeIniciarLote`) MAIS ao menos um card selecionável nela — senão
     * o botão apareceria sem nada para encerrar. Esta segunda parte é do botão, e não do checkbox:
     * o checkbox já nasce dentro de um card.
     */
    podeEncerrarEmLote(coluna: KanbanColuna): boolean {
        return this.colunaPodeIniciarLote(coluna) && coluna.itens.some((item) => this.arrastavel(item));
    }

    /**
     * Pedido de encerrar o lote desta coluna. Sem selecionados não há nada a pedir — e sem coluna
     * conclusiva também não: o lote termina gravando um movimento, e um pedido sem destino abriria o
     * diálogo para uma confirmação que não teria para onde mover nada. É a mesma condição que
     * `podeEncerrarEmLote` já usa para decidir se o botão aparece; repetida aqui porque este método é
     * público e o botão não é a única porta de entrada dele.
     */
    solicitarEncerramentoEmLote(coluna: KanbanColuna): void {
        const itens = Object.values(this.selecionados());
        const colunaDestino = this.colunaConclusiva();
        if (!itens.length || !colunaDestino) {
            return;
        }
        this.encerrarEmLoteSolicitado.emit({ itens, coluna, colunaDestino });
    }

    /** A `KanbanColuna` inteira da conclusiva — `colunaDeConclusao()` devolve só o `KanbanStatus`, e o lote precisa do mesmo tipo que as demais colunas. */
    private colunaConclusiva(): KanbanColuna | undefined {
        const conclusiva = this.colunaDeConclusao();
        return conclusiva ? this.colunas().find((atual) => atual.status.id === conclusiva.id) : undefined;
    }

    constructor() {
        effect(() => {
            const idServico = this.idServico();
            const uf = this.uf();
            if (idServico == null || !uf) {
                return;
            }
            this.carregar(idServico, uf);
        });
    }

    /**
     * Os cards que o quadro tem para distribuir: os protocolos VIVOS que chegam por `itens` mais os
     * CONCLUÍDOS, que vêm por fora.
     *
     * A coluna conclusiva funciona ao contrário das demais, e é por isso que este pool existe.
     * `itens` é a listagem do portal, e ela traz só os vivos (`A` e `T`): ao concluir, o protocolo
     * sai de lá: a linha em `WEB_SOLICITACAO` passava a apontar para a coluna conclusiva e não
     * sobrava card nenhum para colocar nela — a solicitação era finalizada e o card sumia do quadro.
     *
     * Em conflito ganha o CONCLUÍDO: os dois só coexistem na janela entre concluir e a listagem viva
     * recarregar, e nela quem está certo sobre a situação do protocolo é o lado que foi buscado
     * justamente por já constar como concluído.
     */
    private readonly itensDoQuadro = computed<ItemStatus[]>(() => {
        const concluidos = this.concluidos();
        if (!concluidos.length) {
            return this.itens();
        }

        const chavesConcluidas = new Set(concluidos.map((item) => chaveProtocolo(item)));
        return [...concluidos, ...this.itens().filter((item) => !chavesConcluidas.has(chaveProtocolo(item)))];
    });

    /**
     * Não há card nenhum para distribuir — nem vivo nem concluído.
     *
     * A pergunta mora AQUI, e não em quem passa `itens`, porque este é o único lugar que enxerga os
     * dois lados: os concluídos vêm de uma busca própria do quadro, e quem decidisse "está vazio" só
     * pela listagem viva esconderia a coluna Concluído — inclusive a recém-preenchida por um
     * encerramento em lote, que é exatamente quando o analista vai olhar.
     */
    readonly quadroVazio = computed(() => this.itensDoQuadro().length === 0);

    /** Colunas do quadro: cada item cai na coluna do seu status rastreado, ou na primeira se ainda não rastreado. */
    readonly colunas = computed<KanbanColuna[]>(() => {
        const status = this.status();
        if (!status.length) {
            return [];
        }

        const solicitacoes = this.solicitacoes();
        const itens = this.itensDoQuadro();

        // A ORDEM DE RENDERIZAÇÃO é a do array recebido — não mexe aqui. Quem responde "qual é a
        // primeira coluna" para fins de regra de negócio é `primeiraColuna()`, não a posição.
        const filtros = this.filtrosPorColuna();
        // Uma referência de "hoje" para o quadro inteiro: calcular por card faria dois cards da mesma
        // coluna serem medidos contra instantes diferentes.
        const agora = Date.now();

        return status.map((coluna) => {
            const daColuna = itens.filter((item) => this.idColunaDoItem(item, solicitacoes) === coluna.id);
            const filtro = filtros.get(coluna.id);
            const visiveis = daColuna.filter((item) => cardAtendeFiltro(item, solicitacoes.get(chaveProtocolo(item)), filtro, agora));

            return {
                status: coluna,
                itens: this.urgentesPrimeiro(visiveis),
                total: daColuna.length
            };
        });
    });

    /**
     * Colunas carregadas ordenadas por `ordem` — a fonte única de "primeira coluna" e "coluna
     * seguinte" para as regras de negócio. `status()` não vem ordenado do backend, e a POSIÇÃO no
     * array é só a ordem de renderização do quadro; usar `status()[0]` para decidir a menor `ordem`
     * foi o bug que este `computed` fecha: duas regras novas (`destinoPermitido` e `noMeioDoFluxo`)
     * chegaram a discordar sobre qual coluna era a primeira quando o array vinha fora de ordem.
     */
    private readonly statusPorOrdem = computed<KanbanStatus[]>(() => [...this.status()].sort((a, b) => a.ordem - b.ordem));

    /** Coluna de menor `ordem` entre as carregadas. Única definição — todo mundo que precisa da "primeira coluna" lê daqui. */
    private readonly primeiraColuna = computed<KanbanStatus | undefined>(() => this.statusPorOrdem()[0]);

    /**
     * Onde o card vai parar quando o analista conclui pelo menu: a coluna `conclusivo` de menor
     * `ordem` entre as carregadas. Estrutural, pelo DADO — nunca pelo nome, que é cadastro e pode ser
     * renomeado, criado e reordenado em `WEB_STATUS_FLUXO` sem que esta regra mude.
     *
     * O banco já tem uma segunda conclusiva (ARQUIVADO, hoje inativa): concluir pelo menu leva
     * sempre à primeira delas, e tirar o card de lá para outra conclusiva continua sendo trabalho do
     * arraste, que é onde o analista escolhe o destino.
     *
     * `undefined` com o quadro ainda sem colunas, ou com um cadastro sem nenhuma conclusiva — e aí
     * não há para onde concluir, o que o menu respeita não oferecendo as ações.
     */
    readonly colunaDeConclusao = computed<KanbanStatus | undefined>(() => this.statusPorOrdem().find((coluna) => coluna.conclusivo));

    /**
     * A coluna marcada como de pendências (`IN_AVISA_PENDENCIA`), destino de "Avisar pendência" pelo
     * menu. `undefined` num quadro sem nenhuma marcada — e aí o menu não oferece a ação, pelo mesmo
     * motivo de `colunaDeConclusao`: item que abriria um diálogo sem destino é pior que item nenhum.
     */
    readonly colunaDePendencia = computed<KanbanStatus | undefined>(() => this.statusPorOrdem().find((coluna) => coluna.avisaPendencia));

    /**
     * Id da coluna do item: o status rastreado, ou o da coluna de menor `ordem` quando ainda não há
     * linha rastreada. Única resolução — `colunas()` e `statusDoItem()` chamam esta mesma função,
     * para as duas nunca saírem de sincronia.
     */
    private idColunaDoItem(item: ItemStatus, solicitacoes: Map<string, KanbanSolicitacao>): number | undefined {
        const primeira = this.primeiraColuna();
        if (!primeira) {
            return undefined;
        }
        return solicitacoes.get(chaveProtocolo(item))?.idStatus ?? primeira.id;
    }

    /** Coluna atual do card, pronta para as regras de destino e de ações — mesma resolução de `colunas()`. */
    statusDoItem(item: ItemStatus): KanbanStatus | undefined {
        const id = this.idColunaDoItem(item, this.solicitacoes());
        return this.status().find((coluna) => coluna.id === id);
    }

    /**
     * Regra 1: da coluna de menor `ordem`, o único destino aceito é a coluna de `ordem`
     * imediatamente seguinte — pular etapas a partir dali não faz sentido no fluxo. Das demais
     * colunas o destino continua livre, como sempre foi.
     *
     * Estrutural, não por nome: a coluna vem de `WEB_STATUS_FLUXO` e pode ser criada, renomeada e
     * reordenada no banco sem que esta regra precise mudar.
     */
    destinoPermitido(origem: KanbanStatus, destino: KanbanStatus): boolean {
        const primeira = this.primeiraColuna();
        if (!primeira || origem.id !== primeira.id) {
            return true;
        }
        return this.statusPorOrdem()[1]?.id === destino.id;
    }

    /**
     * Regra 2: efetivar e cancelar só fazem sentido no meio do fluxo — antes de começar (coluna de
     * menor `ordem`) não há o que finalizar, e depois de concluir (coluna `conclusivo`) já foi.
     * Mesma resolução de coluna de `statusDoItem()`, e a mesma noção de "primeira" de `destinoPermitido()`.
     */
    private noMeioDoFluxo(item: ItemStatus): boolean {
        const primeira = this.primeiraColuna();
        if (!primeira) {
            return false;
        }
        const atual = this.statusDoItem(item);
        return !!atual && atual.id !== primeira.id && !atual.conclusivo;
    }

    /**
     * Índice de cor da coluna, dado pela POSIÇÃO dela no quadro — a regra mora em
     * `indiceDeCorDoStatus`, compartilhada com o selo que os relatórios usam. Duas implementações da
     * mesma conta é como o quadro pinta uma coluna de azul e o relatório pinta o mesmo status de
     * verde.
     *
     * <p>Coluna que não está na lista cai na cor 0 (e não em "sem cor"): aqui o status SEMPRE vem da
     * própria lista, então isso é defesa em profundidade — a coluna do quadro precisa de alguma cor
     * para desenhar, ao contrário do selo, que pode ficar neutro.</p>
     */
    indiceCor(status: KanbanStatus): number {
        return indiceDeCorDoStatus(this.status(), { id: status.id }) ?? 0;
    }

    /**
     * Criticidade mais alta no topo da coluna. `sort` do JS é estável, então cards de mesmo nível
     * mantêm a ordem em que chegaram — sem isso o quadro se reorganizaria a cada renderização.
     *
     * Ordena pelo NÍVEL (posição na lista ordenada por `ORDEM`), nunca pelo id: id é identidade,
     * não prioridade, e nada garante que o mais urgente tenha o maior id.
     */
    private urgentesPrimeiro(itens: ItemStatus[]): ItemStatus[] {
        return [...itens].sort((a, b) => (this.nivelCriticidade(b) ?? -1) - (this.nivelCriticidade(a) ?? -1));
    }

    /**
     * Posição do card na escala de criticidade: 0 é a de menor `ORDEM`, e o último índice é a mais
     * urgente. Devolve `null` quando o protocolo ainda não é rastreado ou a criticidade dele não
     * está entre as cadastradas — nesse caso o card não recebe faixa.
     *
     * A escala sai do banco: cadastrar um terceiro nível não exige tocar em código nem em CSS além
     * de escolher a cor dele.
     */
    nivelCriticidade(item: ItemStatus): number | null {
        const idCriticidade = this.solicitacoes().get(chaveProtocolo(item))?.idCriticidade;
        if (idCriticidade == null) {
            return null;
        }

        const ordenadas = [...this.criticidades()].sort((a, b) => a.ordem - b.ordem);
        const nivel = ordenadas.findIndex((criticidade) => criticidade.id === idCriticidade);
        return nivel < 0 ? null : nivel;
    }

    /** Sair de uma coluna que inicia atendimento exige encaminhar a alguém. */
    readonly responsavelObrigatorio = computed(() => this.movimentoPendente()?.origem.iniciaAtendimento === true);

    /**
     * Terminalidade vem do DADO (`conclusivo` do status de destino), nunca do nome da coluna — a
     * mesma regra do backend. Coluna renomeada ou criada no cadastro não pode mudar esta decisão.
     */
    readonly destinoEhConclusivo = computed(() => this.movimentoPendente()?.destino.conclusivo === true);

    /**
     * Destino e a coluna de pendencias. Vem da flag do banco (`IN_AVISA_PENDENCIA`), nunca do nome:
     * renomear a coluna nao pode desligar o aviso ao medico em silencio.
     */
    readonly destinoAvisaPendencia = computed(() => this.movimentoPendente()?.destino.avisaPendencia === true);

    /**
     * A ORIGEM já é conclusiva. Vale para `conclusiva -> conclusiva`, que existe de verdade: o banco
     * marca `IN_CONCLUSIVO` em mais de uma coluna, e basta reativar a segunda para o movimento entre
     * elas aparecer no quadro.
     */
    readonly origemEhConclusiva = computed(() => this.movimentoPendente()?.origem.conclusivo === true);

    /**
     * Quando o diálogo de mover tem de perguntar o que fazer com a solicitação no portal.
     *
     * Só quando a solicitação AINDA está em aberto: saindo de uma coluna conclusiva não há o que
     * finalizar, já foi — e exigir a escolha ali tornaria `conclusiva -> conclusiva` um movimento
     * impossível, porque nenhuma das duas ações se aplica a protocolo já finalizado.
     *
     * Além de conclusivo, exige o código do serviço E o id: o código é como `finalizarSolicitacao`
     * resolve o endpoint no portal, e o id é o que `gravarMovimento` exige para gravar o movimento.
     * Os dois vêm do mesmo serviço selecionado e na prática andam juntos, mas a condição os amarra de
     * propósito — sem isso a invariante "card só chega à coluna conclusiva com a solicitação
     * finalizada" dependeria de o `undefined` de um implicar o vazio do outro, que é acidente de
     * quem chama, não regra deste componente.
     */
    readonly exigeEscolhaAoConcluir = computed(() => this.destinoEhConclusivo() && !this.origemEhConclusiva() && !!this.codigoServico() && this.idServico() != null);

    /**
     * Serviço cuja efetivação tem passo próprio que o caminho genérico do facade não reproduz. Aqui
     * ele só nomeia o MOTIVO de a efetivação estar indisponível — quem decide é `podeEfetivar`, e
     * quem responde por quais serviços são é a função do util, nunca uma lista local.
     */
    readonly efetivacaoApenasNoDetalhe = computed(() => efetivacaoSoNaTelaDeDetalhe(this.codigoServico()));

    /** Campo extra que a efetivação deste serviço exige, e o rótulo dele — o mesmo par da tela de detalhe. */
    readonly campoExigidoNaEfetivacao = computed<CampoConfirmacao>(() => campoDaConfirmacao(this.codigoServico()));
    readonly rotuloDoCampoExigido = computed(() => rotuloDoCampoDeConfirmacao(this.codigoServico()));

    /**
     * O que o diálogo pode oferecer ao concluir. É O MESMO gate de domínio do menu do card
     * (`podeEfetivarProtocolo`/`podeCancelarProtocolo`), chamado e não replicado: ele já responde por
     * serviço sem endpoint de efetivação (`area-atuacao`, especialidade, 2ª via de carteira e de
     * cédula só cancelam), por role faltando, por UF (cart-evento fora do RJ) e pela identidade do
     * protocolo. Uma segunda regra aqui sairia de sincronia com aquela, e o diálogo passaria a
     * prometer o que o card já sabe que não dá.
     *
     * O que NÃO entra é `noMeioDoFluxo`: onde o card está é decisão de apresentação do menu do card,
     * e não tem sentido num diálogo cujo propósito é justamente tirá-lo daquela posição. Com ele
     * junto, `primeira -> conclusiva` (quadro de duas colunas) ficava sem ação nenhuma e o movimento
     * era barrado para sempre.
     *
     * O que entra é a ORIGEM do arraste: não se efetiva o que saiu do Backlog. Ali o protocolo ainda
     * não foi puxado por ninguém — não tem responsável e não foi tramitado para `T` —, então concluí-lo
     * seria dar desfecho a um protocolo que nenhum analista abriu. É a mesma objeção que mantém o
     * encerramento em lote sem especificação, e vale igual para o gesto individual.
     *
     * Repare que a trava é pela origem, e não por `noMeioDoFluxo`: o card já está sendo solto na
     * coluna conclusiva, então perguntar onde ele "está" responderia pelo destino. E o movimento não
     * fica sem saída — cancelar continua disponível, que é o desfecho honesto para quem nunca foi
     * atendido.
     *
     * O pagamento entra junto pelo mesmo motivo do card: protocolo não pago não efetiva, aqui como lá.
     */
    readonly podeEfetivarAoConcluir = computed(() => {
        const pendente = this.movimentoPendente();
        if (!pendente || this.saiuDoBacklog(pendente.origem)) {
            return false;
        }
        return this.podeEfetivarProtocolo(pendente.item) && !this.efetivacaoBloqueadaPorPagamento(pendente.item);
    });

    /**
     * A coluna de origem é a de entrada do quadro.
     *
     * Pela flag `iniciaAtendimento`, nunca pelo nome nem pela posição: é ela que o resto do quadro
     * já usa para saber que sair dali exige responsável e tramita o protocolo no portal.
     */
    private saiuDoBacklog(origem: KanbanStatus): boolean {
        return origem.iniciaAtendimento;
    }

    readonly podeCancelarAoConcluir = computed(() => {
        const pendente = this.movimentoPendente();
        return !!pendente && this.podeCancelarProtocolo(pendente.item);
    });

    /**
     * Efetivar o card do movimento aberto vai praticar a inscrição de ofício.
     *
     * É o que o rádio e o aviso do diálogo precisam saber para nomear o ato. Note que ele é
     * independente do que o analista marcou: descreve o que AQUELE rádio faz, não a escolha corrente
     * — quem cuida de não descrever um ato que não vai ocorrer é `avisoDaConclusao`.
     */
    readonly conclusaoSeraExOfficio = computed(() => {
        const pendente = this.movimentoPendente();
        return !!pendente && this.seraExOfficio(pendente.item);
    });

    /**
     * O rótulo do rádio "Efetivar". Nomeia o ato onde ele é ESCOLHIDO: no ofício, marcar "Efetivar"
     * inscreve a empresa sem pagamento, e um rótulo que diz só "Efetivar" esconde a diferença
     * justamente no controle que a decide.
     */
    readonly rotuloDaEfetivacao = computed(() => (this.conclusaoSeraExOfficio() ? 'Inscrever de ofício' : 'Efetivar'));

    /**
     * Nenhuma das duas ações está disponível. Concluir EXIGE uma delas, então o movimento não tem
     * como ser satisfeito e é bloqueado — liberar sem finalizar devolveria exatamente o card
     * concluído com o protocolo em aberto que a ordem portal-primeiro existe para impedir.
     */
    readonly nenhumaAcaoAoConcluir = computed(() => this.exigeEscolhaAoConcluir() && !this.podeEfetivarAoConcluir() && !this.podeCancelarAoConcluir());

    /**
     * O que o portal já finalizou para o protocolo do movimento aberto, ou `null`. É o que congela a
     * escolha na tela: a ação já foi EXECUTADA no portal, e um rádio que a ofereça para troca é um
     * controle que não decide mais nada — trocá-lo descartaria em silêncio o motivo digitado e
     * gravaria o card como concluído com o protocolo efetivado.
     */
    readonly acaoJaFinalizadaNoPortal = computed<AcaoServico | null>(() => {
        const pendente = this.movimentoPendente();
        return pendente ? this.conclusoesNoPortal.acaoAceita(this.chaveDoAceite(pendente.item)) : null;
    });

    /**
     * Como este quadro nomeia um protocolo dentro da memória de aceites do portal. Um método só,
     * usado nos quatro pontos que a leem ou escrevem: montado à mão em cada um, bastaria um esquecer
     * o `codigoServico` para o quadro gravar sob uma chave e reler sob outra — e a proteção contra a
     * segunda finalização sumiria sem nenhum sintoma até acontecer.
     *
     * <p>Rodada de correção 2 (Importante 1): a UF entrou na chave, e vem de `ufDoProtocolo` — a
     * MESMA resolução (própria do protocolo, com a do quadro como fallback) que o lote faz do outro
     * lado. Passar `item.ufCrm` cru aqui reabriria em silêncio o buraco que a UF veio fechar.</p>
     */
    private chaveDoAceite(item: ItemStatus): string {
        return chaveDaConclusao(this.codigoServico(), this.ufDoProtocolo(item), chaveProtocolo(item));
    }

    /**
     * A explicação na tela do que está indisponível e por quê. Radio apagado (ou movimento barrado)
     * sem motivo vira chamado de suporte — e o motivo aqui é sempre o de um caso só, o mais
     * específico primeiro.
     */
    readonly avisoDaConclusao = computed<string | null>(() => {
        const pendente = this.movimentoPendente();
        if (!pendente || !this.exigeEscolhaAoConcluir()) {
            return null;
        }
        // Mais específico que todos os demais: aqui não há escolha a fazer, e sim uma pendência a
        // explicar. Sem isto o analista vê os dois rádios apagados e nenhuma razão para isso.
        const jaFinalizada = this.acaoJaFinalizadaNoPortal();
        if (jaFinalizada) {
            return `A solicitação já foi ${jaFinalizada === 'efetivar' ? 'efetivada' : 'cancelada'} no portal e isso não se desfaz. Falta apenas registrar o movimento no quadro: confirme para mover o card.`;
        }
        if (this.nenhumaAcaoAoConcluir()) {
            return 'Nem efetivar nem cancelar estão disponíveis para este protocolo, e concluir exige uma das duas. Este movimento não pode ser feito pelo quadro.';
        }
        if (!this.podeEfetivarAoConcluir()) {
            return this.motivoSemEfetivacao(pendente.item);
        }
        // O ato mudou de natureza: aqui a efetivação inscreve a empresa SEM pagamento. Vem antes da
        // indisponibilidade do cancelar porque aquela é informação sobre o que falta, e esta é
        // informação sobre o que vai acontecer se ele confirmar.
        //
        // Cala quando a escolha é cancelar: a mensagem descreve a inscrição de ofício, e com o
        // cancelamento marcado ela estaria descrevendo um ato que não vai ocorrer — pior que
        // silêncio, porque o analista confirmaria lendo o texto do ato errado.
        if (this.conclusaoSeraExOfficio() && this.acaoAoConcluir() !== 'cancelar') {
            return mensagemConfirmacaoExOfficio(pendente.item, Date.now());
        }
        return this.podeCancelarAoConcluir() ? null : 'Cancelar não está disponível para este protocolo.';
    });

    /** Por que a efetivação está fora. Só nomeia o motivo — a decisão é de `podeEfetivar`. */
    private motivoSemEfetivacao(item: ItemStatus): string {
        if (this.efetivacaoApenasNoDetalhe()) {
            return 'A efetivação deste serviço é feita na tela do protocolo.';
        }
        // Antes do pagamento: quem vem do Backlog nem chega a ser avaliado por dinheiro, e dizer
        // "sem pagamento" mandaria o analista atrás do boleto quando o que falta é atender.
        const pendente = this.movimentoPendente();
        if (pendente && this.saiuDoBacklog(pendente.origem)) {
            return 'Protocolo ainda no Backlog: puxe para atendimento antes de concluir.';
        }
        if (this.efetivacaoBloqueadaPorPagamento(item)) {
            return MOTIVO_SEM_PAGAMENTO;
        }
        // Serviço mapeado sem a role de confirmar é serviço que só cancela — não tem endpoint de
        // efetivação nenhum, e prometer "Efetivar" ali cairia em "serviço sem endpoint mapeado".
        const roles = rolesDoServico(this.codigoServico());
        if (roles && !roles.confirmar) {
            return 'Este serviço não tem efetivação: a solicitação só pode ser cancelada.';
        }
        return 'Você não tem permissão para efetivar solicitações deste serviço.';
    }

    readonly podeConfirmar = computed(() => {
        if (this.exigeEscolhaAoConcluir() && !this.escolhaDaConclusaoCompleta()) {
            return false;
        }
        return !this.responsavelObrigatorio() || this.responsavelSelecionado() !== null;
    });

    /**
     * A escolha só está completa quando a ação está DISPONÍVEL para este protocolo e o campo que ELA
     * exige está preenchido: motivo no cancelar, data ou observação no efetivar. O `[disabled]` do
     * rádio é a experiência; esta é a garantia, para quem chegar por outro caminho.
     */
    private escolhaDaConclusaoCompleta(): boolean {
        // O portal já executou a escolha. Os campos que ela exigia não são mais editáveis, e travar o
        // Confirmar por causa deles travaria a única coisa que ainda falta — gravar o movimento —,
        // sem nada na tela explicando por quê.
        if (this.acaoJaFinalizadaNoPortal()) {
            return true;
        }

        const acao = this.acaoAoConcluir();
        if (!acao) {
            return false;
        }
        if (acao === 'cancelar') {
            return this.podeCancelarAoConcluir() && !!this.motivoDaConclusao().trim();
        }
        if (!this.podeEfetivarAoConcluir()) {
            return false;
        }
        return this.campoExigidoNaEfetivacao() === 'nenhum' || !!this.dadoDaEfetivacao().trim();
    }

    /** Urgente é a criticidade de MAIOR ordem, e normal a de menor — nunca um id fixo. */
    readonly criticidadeUrgente = computed(() => {
        const ordenadas = [...this.criticidades()].sort((a, b) => a.ordem - b.ordem);
        return ordenadas.length > 1 ? ordenadas[ordenadas.length - 1] : null;
    });

    readonly criticidadeNormal = computed(() => {
        const ordenadas = [...this.criticidades()].sort((a, b) => a.ordem - b.ordem);
        return ordenadas.length ? ordenadas[0] : null;
    });

    /**
     * Busca a foto só dos responsáveis efetivamente atribuídos, sem repetir id. A foto é um LOB no
     * banco: pedir a lista inteira de elegíveis da UF traria a imagem de quem não tem card nenhum.
     *
     * <p>Quem guarda e busca é o {@link FotosResponsaveisService}, o mesmo cache do avatar dos
     * relatórios — a deduplicação, a mesclagem e a falha silenciosa moram lá. Falha aqui não derruba
     * o quadro: o avatar cai nas iniciais e o erro vai para o log.</p>
     *
     * <p>O quadro <b>não zera mais</b> o cache ao trocar de serviço, e a chamada não carrega o guard
     * de corrida (`aindaCorrente`) que as outras cargas usam. Os dois existiam porque o mapa era
     * local e recomeçava a cada carregamento: uma resposta atrasada repovoaria um mapa que tinha
     * acabado de ser esvaziado. Num cache por id de usuário isso deixa de ser risco — a foto de quem
     * quer que seja é a mesma em qualquer quadro, então resposta atrasada só acrescenta uma entrada
     * correta, e o que sobra de um serviço anterior nunca é lido (a leitura é sempre por um id que
     * está na tela).</p>
     */
    private carregarFotosDosResponsaveis(solicitacoes: KanbanSolicitacao[]): void {
        this.fotos.carregar(solicitacoes.map((s) => s.idWebUsuarioResponsavel).filter((id): id is number => id != null));
    }

    /** Nome do responsável do card, vindo do mapa de solicitações rastreadas. */
    nomeResponsavel(item: ItemStatus): string | undefined {
        return this.solicitacoes().get(chaveProtocolo(item))?.nomeResponsavel;
    }

    /**
     * Foto do responsável como data URI, ou `null` quando ele não tem foto cadastrada — nesse caso
     * o avatar cai nas iniciais. O backend só devolve quem tem foto, então ausência aqui é ausência
     * de verdade, não erro de carga.
     */
    fotoResponsavel(item: ItemStatus): string | null {
        const id = this.solicitacoes().get(chaveProtocolo(item))?.idWebUsuarioResponsavel;
        return id == null ? null : this.fotoResponsavelPorId(id);
    }

    /**
     * Foto do médico do card, ou `null` enquanto não chegou (ou se ele não tem foto cadastrada).
     *
     * `item.avatar` tem precedência: alguns portais já devolvem a foto junto do protocolo, e nesse
     * caso não há consulta a fazer.
     */
    fotoMedico(item: ItemStatus): string | null {
        if (item.avatar) {
            return item.avatar;
        }

        const chave = this.chaveMedico(item);
        return (chave && this.fotosMedicos().get(chave)) || null;
    }

    /**
     * Busca a foto do médico deste card. Chamada quando o card APARECE na tela, e não no
     * carregamento do quadro: o backlog pode ter centenas de cards, e buscar todas de uma vez seriam
     * centenas de requisições — a maioria de cards que ninguém rola até ver.
     *
     * Falha silenciosa: o card simplesmente fica sem foto, e o erro vai para o log. Médico sem foto
     * é o caso comum, não uma exceção.
     */
    carregarFotoDoMedico(item: ItemStatus): void {
        if (item.avatar) {
            return;
        }

        const chave = this.chaveMedico(item);
        const crmUf = separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), item), item.ufCrm);
        if (!chave || !crmUf || this.medicosConsultados.has(chave)) {
            return;
        }

        this.medicosConsultados.add(chave);
        this.biometriaService
            .buscarFotoPorCrmUf(crmUf.crm, crmUf.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resposta) => {
                    const base64 = resposta?.imagemBase64;
                    if (!base64) {
                        return;
                    }
                    const foto = base64.startsWith('data:') ? base64 : `data:image/jpeg;base64,${base64}`;
                    this.fotosMedicos.update((atual) => new Map(atual).set(chave, foto));
                },
                error: (falha: unknown) => this.logger.error('Falha ao carregar a foto do médico:', falha)
            });
    }

    /**
     * Iniciais do médico para o avatar sem foto, como já se faz com o responsável — médico sem foto
     * é o caso comum, e o card ficava com um buraco no lugar do avatar.
     *
     * Na pessoa jurídica o retratado é o DIRETOR TÉCNICO, não a empresa (`item.empresa`): cair nas
     * iniciais dela seria mostrar as iniciais de quem não é o retratado — e um avatar redondo com as
     * iniciais de uma empresa sugere uma pessoa que não existe. Sem diretor informado sai vazio, e o
     * card não desenha avatar nenhum.
     */
    iniciaisMedico(item: ItemStatus): string {
        const nome = rostoDoCardEhDoDiretorTecnico(this.tipoPessoa()) ? item.diretorTecnicoNome : item.empresa;
        return this.iniciaisDoNome(nome);
    }

    /**
     * Rótulo próprio do avatar do médico, quando ele NÃO é decorativo. Só a pessoa jurídica tem: ali
     * o rosto é do diretor técnico, e o nome ao lado do avatar é o da EMPRESA — sem rótulo, nem o
     * leitor de tela nem quem enxerga sabem de quem é aquele rosto.
     *
     * Vazio na pessoa física: ali a foto retrata exatamente o nome que já está ao lado dela, e o
     * template mantém o avatar decorativo (`aria-hidden`, `alt` vazio) — anunciá-lo repetiria o que
     * já se lê.
     */
    rotuloAvatarMedico(item: ItemStatus): string {
        if (!rostoDoCardEhDoDiretorTecnico(this.tipoPessoa())) {
            return '';
        }
        const nome = (item.diretorTecnicoNome ?? '').trim();
        return nome ? `Diretor técnico: ${nome}` : '';
    }

    /**
     * Chave da foto: o CRM identifica o médico, e o mesmo médico pode ter vários protocolos. Vem de
     * `crmUfDoRostoDoCard`, a MESMA resolução que `carregarFotoDoMedico` usa para buscar — nunca uma
     * segunda leitura própria, que poderia divergir e guardar a foto de alguém sob a chave de outro.
     */
    private chaveMedico(item: ItemStatus): string | null {
        const crmUf = separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), item), item.ufCrm);
        return crmUf && `${crmUf.crm}-${crmUf.uf}`;
    }

    /**
     * Mesma foto, a partir do id — o seletor do diálogo não tem um `ItemStatus` em mãos.
     *
     * <p>Lê o sinal do cache compartilhado, então o template OnPush continua reagindo à chegada das
     * fotos: sinal lido durante a renderização vira dependência da view, esteja ele onde estiver.</p>
     */
    fotoResponsavelPorId(idWebUsuario: number): string | null {
        return this.fotos.fotoPorId(idWebUsuario);
    }

    /**
     * Iniciais do responsável para o avatar sem foto. Primeira e última palavra do nome — "Ana
     * Souza" vira "AS", "Ana" vira "A".
     */
    iniciaisResponsavel(item: ItemStatus): string {
        return this.iniciaisDoNome(this.nomeResponsavel(item));
    }

    /**
     * Primeira e última palavra do nome: "Ana Souza" vira "AS", "Ana" vira "A".
     *
     * <p>A regra mora em `avatar-responsavel.presenter`, compartilhada com o avatar que o relatório
     * de carga usa: duas implementações é como o mesmo nome vira "AS" numa tela e "AN" na outra.</p>
     */
    iniciaisDoNome(nome?: string): string {
        return iniciaisDoNome(nome);
    }

    ehUrgente(item: ItemStatus): boolean {
        const urgente = this.criticidadeUrgente();
        if (!urgente) {
            return false;
        }
        return this.solicitacoes().get(chaveProtocolo(item))?.idCriticidade === urgente.id;
    }

    /**
     * Busca as colunas e, em seguida, as solicitações rastreadas da UF; aborta em erro sem tentar o
     * segundo passo. Cada resposta só é aplicada se `idServico`/`uf` ainda forem os correntes — uma
     * troca rápida de serviço/UF não deixa a resposta antiga sobrescrever a mais nova ("last request wins").
     */
    carregar(idServico: number, uf: string): void {
        // A seleção do lote não sobrevive a uma recarga: os cards marcados podem não ser mais os que
        // a resposta nova traz.
        this.limparSelecionados();
        this.carregando.set(true);
        this.erro.set(null);

        const aindaCorrente = (): boolean => this.idServico() === idServico && this.uf() === uf;

        this.carregarConcluidos(idServico, uf, aindaCorrente);

        // Criticidades e responsáveis alimentam o selo e o seletor; uma falha aqui não impede o
        // quadro de abrir, então não derruba o carregamento nem escreve em `erro`.
        this.kanbanServicosService
            .listarCriticidades()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (criticidades) => {
                    if (aindaCorrente()) {
                        this.criticidades.set(criticidades);
                    }
                },
                error: (falha: unknown) => this.logger.error('Falha ao carregar as criticidades do kanban:', falha)
            });

        this.kanbanServicosService
            .listarResponsaveis(uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (responsaveis) => {
                    if (aindaCorrente()) {
                        this.responsaveis.set(responsaveis);
                    }
                },
                error: (falha: unknown) => this.logger.error('Falha ao carregar os responsáveis elegíveis:', falha)
            });

        this.kanbanServicosService
            .listarStatus()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (status) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    // Quadro sem colunas nao e "nenhum protocolo": e configuracao faltando. Sem isto
                    // a tela fica em branco, e o analista conclui que nao ha trabalho.
                    if (!status.length) {
                        this.carregando.set(false);
                        this.status.set([]);
                        this.erro.set('O quadro não tem colunas configuradas. Procure o administrador do sistema.');
                        return;
                    }
                    this.status.set(status);
                    this.kanbanServicosService
                        .listarSolicitacoes(idServico, uf)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (solicitacoes) => {
                                if (!aindaCorrente()) {
                                    return;
                                }
                                this.solicitacoes.set(new Map(solicitacoes.map((s) => [chaveNumeroAno(s.ano, s.numero), s])));
                                this.carregando.set(false);
                                this.carregarFotosDosResponsaveis(solicitacoes);
                            },
                            error: (falha: { error?: { title?: string } }) => {
                                if (!aindaCorrente()) {
                                    return;
                                }
                                this.carregando.set(false);
                                this.logger.error('Falha ao carregar o andamento das solicitações do kanban:', falha);
                                this.erro.set(falha?.error?.title ?? 'Não foi possível carregar o andamento das solicitações.');
                            }
                        });
                },
                error: (falha: { error?: { title?: string } }) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    this.carregando.set(false);
                    this.status.set([]);
                    this.logger.error('Falha ao carregar as colunas do kanban:', falha);
                    this.erro.set(falha?.error?.title ?? 'Não foi possível carregar as colunas do quadro.');
                }
            });
    }

    /**
     * Busca os concluídos dos últimos 30 dias e os mapeia com o MESMO mapper da listagem viva — o
     * endpoint devolve o objeto cru do portal justamente para isso.
     *
     * Falha aqui não derruba o quadro nem escreve em `erro`, como criticidades e responsáveis: as
     * outras colunas continuam úteis, e o erro vai para o log. O que ela custa é a coluna Concluído
     * abrir vazia, não o quadro não abrir.
     */
    private carregarConcluidos(idServico: number, uf: string, aindaCorrente: () => boolean): void {
        this.kanbanServicosService
            .listarConcluidos(idServico, uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (respostas) => {
                    if (aindaCorrente()) {
                        this.concluidos.set(respostas.map((resposta, indice) => mapearItemStatusProtocolo(resposta, indice, uf)));
                    }
                },
                error: (falha: unknown) => this.logger.error('Falha ao carregar os protocolos concluídos do quadro:', falha)
            });
    }

    recarregar(): void {
        // A limpeza mora só em carregar(): recarregar() sempre passa por ali quando idServico/uf
        // estão preenchidos, e chamar aqui também seria redundante — sem efeito observável, só ruído.
        const idServico = this.idServico();
        const uf = this.uf();
        if (idServico != null && uf) {
            this.carregar(idServico, uf);
        }
    }

    /**
     * Sem número/ano não há chave para gravar, então o card fica parado (arraste desabilitado). E o
     * perfil nacional não move card nenhum: ele acompanha, não executa.
     *
     * <p>Fecha o arraste do card, e junto o botão "Encerrar em lote" (`podeEncerrarEmLote` só se
     * ativa quando ALGUM item da coluna é `arrastavel`). O checkbox, porém, NÃO deriva daqui:
     * `colunaPodeIniciarLote` é uma pergunta por COLUNA ("esta posição no fluxo pode originar um
     * lote?"), não por card, e não olha item nenhum — por isso tem sua própria checagem abaixo, em
     * vez de herdar esta.</p>
     */
    arrastavel(item: ItemStatus): boolean {
        if (this.ufEfetivaStore.somenteLeitura()) {
            return false;
        }
        return inteiro(item.numeroProtocolo) !== null && inteiro(item.anoProtocolo) !== null;
    }

    /**
     * Identificador exibido no card e no rótulo acessível: ano/número quando os dois estão
     * presentes; senão o portal, que o mapper garante sempre preenchido (ex.: `ABERTO-<n>`) — e
     * evita um card rotulado só `#/` ou um rótulo acessível "undefined/undefined". A regra vive no
     * util porque o painel de detalhe desenha o mesmo identificador no mesmo formato de card.
     */
    identificadorProtocolo(item: ItemStatus): string {
        return identificadorProtocoloUtil(item);
    }

    totalAnotacoes(item: ItemStatus): number {
        return this.solicitacoes().get(chaveProtocolo(item))?.totalAnotacoes ?? 0;
    }

    /**
     * Rótulo do balão na faixa do pé. O número sozinho não dizia de que ele era contagem — o ícone
     * era a única pista, e ícone não se lê em voz alta.
     */
    rotuloAnotacoes(item: ItemStatus): string {
        const total = this.totalAnotacoes(item);
        return `${total} ${total === 1 ? 'comentário' : 'comentários'}`;
    }

    /**
     * A linha sob o nome do card. Identifica QUEM solicitou, que é a pergunta seguinte à do nome:
     * dois médicos homônimos só se separam pelo CRM.
     *
     * CRM/UF vale para pessoa física e jurídica — a empresa cadastrada também tem CRM, e o mapper
     * já lê `crmEmpresa` como primeira alternativa. O pré-prestador é a exceção: ali a empresa ainda
     * não foi registrada, não há CRM nenhum, e o que a identifica é o CNPJ.
     *
     * Vazio faz a linha sumir, sem placeholder: no pré-prestador o CNPJ vem nulo com frequência, e
     * um "não informado" repetido em todo card seria ruído em vez de informação.
     */
    identificacaoDoSolicitante(item: ItemStatus): string {
        if (identificacaoPorCnpj(this.codigoServico())) {
            const cnpj = (item.cnpj ?? '').trim();
            // Idempotente: o mapper já entrega com máscara, e formatar de novo protege o card de
            // quem construa o item por outro caminho (diálogos, testes, futuro mapper de outro portal).
            return cnpj ? `CNPJ ${MascaraUtil.cpfCnpj(cnpj)}` : '';
        }

        // Split próprio em vez de `separarCrmUf`: aquele converte o CRM para número, porque monta a
        // URL da foto. Aqui o CRM é texto exibido, e zero à esquerda cadastrado tem de aparecer.
        const [crm, uf] = (item.crmUf ?? '').trim().split('-');
        // `||`, e nao `??`: `crmUf` no formato `203629-` produz UF vazia, e não `undefined`. Com o
        // `??` o fallback seria pulado e a linha sumiria mesmo com a UF do quadro em mãos.
        const ufResolvida = (uf || item.ufCrm || '').trim();
        return crm && ufResolvida ? `CRM ${crm.trim()}/${ufResolvida.toUpperCase()}` : '';
    }

    /**
     * Itens do menu de três pontos do card. Efetivar e cancelar saíram da faixa do pé: eram os dois
     * únicos botões com texto ali e competiam com "Visualizar" por atenção, sem ter a mesma
     * frequência de uso.
     *
     * A lista vazia é significativa — o template não desenha o kebab quando ela vem assim, porque um
     * menu que abre vazio é pior que menu nenhum. As regras de quem vê o quê continuam em
     * `podeEfetivar`/`podeCancelar`: aqui só se decide o formato, não a permissão.
     */
    acoesDoCard(item: ItemStatus): MenuItem[] {
        const acoes: MenuItem[] = [];

        // Efetivar e Cancelar CONCLUEM o card, e concluir precisa de uma coluna conclusiva para onde
        // ir. Sem ela as duas somem — item que abriria um diálogo sem destino é pior que item nenhum.
        // A guarda envolve só as duas: "Avisar pendência" tem destino próprio e não depende disto.
        if (this.colunaDeConclusao()) {
            if (this.podeEfetivar(item)) {
                const bloqueada = this.efetivacaoBloqueadaPorPagamento(item);
                acoes.push({
                    label: 'Efetivar',
                    icon: 'pi pi-check',
                    // Visível e inerte, como era o botão: a ação existe para este analista, só não para
                    // este protocolo, e o `title` diz qual é a pendência.
                    disabled: bloqueada,
                    title: bloqueada ? MOTIVO_SEM_PAGAMENTO : undefined,
                    command: () => this.concluirPeloMenu(item, 'efetivar')
                });
            }

            if (this.podeCancelar(item)) {
                // Sem cor própria de propósito: o popup do menu é anexado ao body e sai do escopo do
                // componente, então o que distingue as duas ações aqui é o ícone (check x proibido).
                acoes.push({ label: 'Cancelar', icon: 'pi pi-ban', command: () => this.concluirPeloMenu(item, 'cancelar') });
            }
        }

        if (this.podeAvisarPendencia(item)) {
            acoes.push({ label: 'Avisar pendência', icon: 'pi pi-comments', command: () => this.avisarPendenciaPeloMenu(item) });
        }

        return acoes;
    }

    /**
     * O card está em tratamento e pode gerar aviso de pendência.
     *
     * A regra sai das flags da coluna, nunca do nome dela: vale para a coluna em que o protocolo
     * está sendo trabalhado e para a própria coluna de pendências. Ficam de fora a coluna que INICIA
     * o atendimento — lá o protocolo ainda não foi puxado por ninguém, e no portal ele nem saiu de
     * `A` — e as conclusivas, onde a solicitação já acabou e não há pendência a cobrar.
     *
     * Guarda com `somenteLeitura()`, e não com `!arrastavel(item)` como `podeEfetivarProtocolo` e
     * `podeCancelarProtocolo`: `arrastavel` carrega permissão e posição no quadro, condições que esta
     * ação nunca teve — herdá-las mudaria o comportamento do usuário REGIONAL aqui, o que ninguém
     * pediu. A assimetria com os dois irmãos é deliberada.
     */
    podeAvisarPendencia(item: ItemStatus): boolean {
        if (this.ufEfetivaStore.somenteLeitura()) {
            return false;
        }
        const origem = this.statusDoItem(item);
        return !!origem && !!this.colunaDePendencia() && !origem.conclusivo && !origem.iniciaAtendimento;
    }

    /**
     * Avisar pendência pelo menu do card, sem depender de o analista lembrar do checkbox ao arrastar.
     *
     * Dois caminhos, conforme onde o card está — e nenhum deles é novo:
     *
     * - FORA da coluna de pendências: abre o MESMO diálogo do arraste, com destino em pendências e o
     *   aviso JÁ marcado. O card ainda precisa se mover, e mover é o que aquele diálogo faz — com a
     *   criticidade, a anotação e a confirmação que o analista espera ver antes de um e-mail sair.
     * - JÁ na coluna de pendências: não há movimento a fazer, então o pedido de aviso é emitido
     *   direto. Abrir um diálogo de mover para a coluna em que o card já está pediria uma decisão
     *   que não existe.
     */
    avisarPendenciaPeloMenu(item: ItemStatus): void {
        const origem = this.statusDoItem(item);
        const destino = this.colunaDePendencia();
        if (!origem || !destino || !this.podeAvisarPendencia(item)) {
            return;
        }

        if (origem.id === destino.id) {
            this.avisoPendenciaSolicitado.emit(item);
            return;
        }

        this.iniciarMovimento(item, origem, destino);
        // DEPOIS de abrir, como em `concluirPeloMenu`: `iniciarMovimento` passa por `cancelarMovimento`,
        // que zera a marcação — marcar antes seria perdê-la.
        this.avisarMedico.set(true);
    }

    /**
     * Efetivar ou cancelar pelo menu do card. Não tem fluxo próprio: abre O MESMO diálogo do arraste,
     * já com a ação marcada, e o card sai para a coluna conclusiva como sairia arrastado.
     *
     * Convergir, e não duplicar, é o ponto. Os dois caminhos executavam a mesma sequência com
     * modelos de recuperação incompatíveis — um recarregava a lista de protocolos, o outro contava
     * com o card continuar na tela —, e o resultado foi solicitação finalizada no portal, card sumido
     * do quadro e toast verde de sucesso. Dar ao menu o seu próprio "mover" recriaria a duplicação
     * com quatro passos em vez de dois; daqui para a frente há um caminho só, e ele já sabe chamar o
     * portal antes, gravar o movimento só com o OK, guardar o aceite quando a gravação falha e
     * congelar a escolha que o portal já executou.
     */
    concluirPeloMenu(item: ItemStatus, acao: AcaoServico): void {
        const origem = this.statusDoItem(item);
        const destino = this.colunaDeConclusao();
        if (!origem || !destino) {
            return;
        }

        this.iniciarMovimento(item, origem, destino);
        // DEPOIS de abrir, nunca antes: `iniciarMovimento` limpa a escolha da conclusão, e marcar
        // antes dele seria perder a marcação — o diálogo abriria com os dois rádios em branco.
        //
        // E não sobrescreve o que o portal JÁ executou para este protocolo: aquilo não se desfaz, é
        // o que `iniciarMovimento` acabou de marcar e o template congela. Trocar a marcação aqui
        // mostraria no rádio uma ação diferente da que `confirmarMovimento` vai registrar.
        if (!this.acaoJaFinalizadaNoPortal()) {
            this.acaoAoConcluir.set(acao);
        }
    }

    /**
     * Id do popup do menu deste card, alvo do `aria-controls` do kebab. Sem ele o leitor de tela sabe
     * que o botão abre um menu, mas não QUAL — e há um menu por card na mesma página.
     *
     * O identificador do portal traz `/` (`2026/143`), então tudo que não é letra, número, `_` ou `-`
     * vira `-`: `getElementById` toleraria a barra, mas um id sanitizado continua utilizável em
     * seletor CSS e em teste.
     */
    idDoMenuDoCard(item: ItemStatus): string {
        return `kanban-menu-${(item.portal ?? '').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    }

    /**
     * Abre/fecha o popup e registra o estado REAL dele, lido do próprio menu logo após o toggle —
     * `aria-expanded` que não acompanha o popup mente para o leitor de tela, e mentir é pior que
     * omitir. O fechamento por clique fora não passa por aqui: quem o cobre é `aoFecharMenuDoCard`,
     * ligado ao `onHide` do popup.
     */
    alternarMenuDoCard(menu: Menu, item: ItemStatus, evento: Event): void {
        menu.toggle(evento);
        this.menuAbertoDoCard.set(menu.visible ? item.portal : null);
    }

    /** O popup fechou por qualquer via (clique fora, ESC, escolha de um item). */
    aoFecharMenuDoCard(item: ItemStatus): void {
        if (this.menuAbertoDoCard() === item.portal) {
            this.menuAbertoDoCard.set(null);
        }
    }

    /**
     * Resumo da última tramitação do protocolo no portal: mesma formatação de data usada no painel de
     * detalhe (dashboard-servicos). Vazio quando não há tramitação ou data — o template decide, com
     * base nisso, se mostra a linha; diferente do painel de detalhe, o card não usa rótulo de "Não
     * informada": a linha ausente some, igual às demais linhas de dados do card.
     */
    ultimaTramitacaoResumo(item: ItemStatus): string {
        return formatarDataTramitacao(item.ultimaTramitacao?.dataTramitacao);
    }

    /**
     * Nome do responsável a partir do id. O backend devolve o id que gravou; o nome é rótulo, e a
     * lista de elegíveis já está carregada para o seletor.
     */
    private nomeDoResponsavel(idWebUsuario?: number): string | undefined {
        if (idWebUsuario == null) {
            return undefined;
        }
        return this.responsaveis().find((responsavel) => responsavel.idWebUsuario === idWebUsuario)?.nome;
    }

    /**
     * Abre a linha do tempo do protocolo. A busca é sempre feita na hora: o histórico muda a cada
     * movimento e guardar em cache mostraria um andamento desatualizado logo depois de mover.
     */
    abrirHistorico(item: ItemStatus): void {
        const numero = inteiro(item.numeroProtocolo);
        const ano = inteiro(item.anoProtocolo);

        // O diálogo repete o avatar do card. Normalmente a foto já chegou (o card estava na tela
        // para ser clicado), mas pedir aqui torna o diálogo independente disso — e a consulta não
        // repete, o médico já consultado é ignorado.
        this.carregarFotoDoMedico(item);
        this.historicoAberto.set(item);
        this.historico.set([]);

        if (numero === null || ano === null) {
            return;
        }

        this.carregandoHistorico.set(true);
        this.kanbanServicosService
            .listarHistorico(ano, numero)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (linhas) => {
                    // Só aplica se o diálogo ainda for o deste protocolo: abrir outro card durante a
                    // requisição não pode fazer o histórico antigo aterrissar no card novo.
                    if (this.historicoAberto() === item) {
                        this.historico.set(linhas);
                        this.carregandoHistorico.set(false);
                        this.carregarFotosDoHistorico(linhas);
                    }
                },
                error: (falha: { error?: { title?: string } }) => {
                    if (this.historicoAberto() !== item) {
                        return;
                    }
                    this.carregandoHistorico.set(false);
                    this.fecharHistorico();
                    this.logger.error('Falha ao carregar o histórico do protocolo:', falha);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: falha?.error?.title ?? 'Não foi possível carregar o histórico.'
                    });
                }
            });
    }

    /**
     * Acrescenta um andamento à linha do tempo do protocolo aberto.
     *
     * Recarrega o histórico em vez de inserir a linha na lista local: quem monta a linha (data, nome
     * de quem escreveu, posição na ordem) é o backend, e montá-la aqui seria adivinhar os três — e
     * divergir da que apareceria no próximo carregamento.
     */
    /**
     * Abre a escrita do andamento. Sempre em branco: rascunho herdado sairia como andamento novo.
     *
     * Guarda `somenteLeitura()` própria (não herdada de `podeInserirAndamento`): este método abre o
     * COMPOSITOR, antes de haver texto nenhum para `podeInserirAndamento` julgar.
     */
    abrirNovoAndamento(): void {
        if (this.ufEfetivaStore.somenteLeitura() || !this.protocoloDoHistoricoRastreado()) {
            return;
        }
        this.novoAndamento.set('');
        this.encaminharPara.set(null);
        // O seletor mostra a foto de cada elegível, e o mapa só tem quem está atribuído em algum
        // card. Busca sob demanda, ao abrir, como no diálogo de mover.
        this.fotos.carregar(this.responsaveis().map((responsavel) => responsavel.idWebUsuario));
        this.escrevendoAndamento.set(true);
    }

    fecharNovoAndamento(): void {
        this.escrevendoAndamento.set(false);
        this.novoAndamento.set('');
        this.encaminharPara.set(null);
    }

    inserirAndamento(): void {
        const item = this.historicoAberto();
        const texto = this.novoAndamento().trim();
        if (!item || !texto || !this.podeInserirAndamento()) {
            return;
        }

        const numero = inteiro(item.numeroProtocolo);
        const ano = inteiro(item.anoProtocolo);
        if (numero === null || ano === null) {
            return;
        }

        const destino = this.encaminharPara();
        this.salvandoAndamento.set(true);
        this.kanbanServicosService
            .anotar({ ano, numero, texto, idWebUsuarioResponsavel: destino ?? undefined })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.salvandoAndamento.set(false);
                    // O diálogo pode ter sido trocado durante a gravação. O andamento FOI gravado —
                    // o que não pode é limpar o campo e recarregar o histórico de outro protocolo.
                    if (this.historicoAberto() !== item) {
                        return;
                    }
                    this.fecharNovoAndamento();
                    // Encaminhou: o card precisa mostrar o dono novo sem esperar o próximo F5.
                    if (destino !== null) {
                        this.solicitacoes.update((mapa) => {
                            const chave = chaveProtocolo(item);
                            const atual = mapa.get(chave);
                            if (!atual) {
                                return mapa;
                            }
                            const atualizado = new Map(mapa);
                            atualizado.set(chave, { ...atual, idWebUsuarioResponsavel: destino, nomeResponsavel: this.nomeDoResponsavel(destino) });
                            return atualizado;
                        });
                    }
                    // Mantém o contador local coerente: é dele que sai o filtro "com anotação", e
                    // recarregar o quadro inteiro por causa de uma linha seria caro demais.
                    this.solicitacoes.update((mapa) => {
                        const chave = chaveProtocolo(item);
                        const atual = mapa.get(chave);
                        if (!atual) {
                            return mapa;
                        }
                        const atualizado = new Map(mapa);
                        atualizado.set(chave, { ...atual, totalAnotacoes: (atual.totalAnotacoes ?? 0) + 1 });
                        return atualizado;
                    });
                    this.recarregarHistorico(item);
                },
                error: (falha: { error?: { title?: string } }) => {
                    this.salvandoAndamento.set(false);
                    this.logger.error('Falha ao inserir andamento no protocolo:', falha);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: falha?.error?.title ?? 'Não foi possível inserir o andamento.'
                    });
                }
            });
    }

    /**
     * Recarrega a linha do tempo do protocolo já aberto.
     *
     * Separado de `abrirHistorico` porque aqui NÃO se mexe em `historicoAberto` nem se esvazia a
     * lista: o diálogo está na tela, e piscar para vazio entre a gravação e a resposta seria pior do
     * que esperar com o conteúdo antigo.
     */
    private recarregarHistorico(item: ItemStatus): void {
        const numero = inteiro(item.numeroProtocolo);
        const ano = inteiro(item.anoProtocolo);
        if (numero === null || ano === null) {
            return;
        }

        this.carregandoHistorico.set(true);
        this.kanbanServicosService
            .listarHistorico(ano, numero)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (linhas) => {
                    if (this.historicoAberto() !== item) {
                        return;
                    }
                    this.historico.set(linhas);
                    this.carregandoHistorico.set(false);
                    this.carregarFotosDoHistorico(linhas);
                },
                error: (falha: unknown) => {
                    this.carregandoHistorico.set(false);
                    this.logger.error('Falha ao recarregar o histórico do protocolo:', falha);
                }
            });
    }

    /**
     * Busca as fotos de quem aparece no histórico. Elas não vêm com o quadro: `carregarFotosDosResponsaveis`
     * traz só quem está atribuído AGORA aos cards, e o histórico é justamente a lista de quem passou por ele
     * — inclusive quem já saiu.
     *
     * O histórico identifica a pessoa pelo NOME e a foto é indexada por id, então o cruzamento é pelo nome
     * normalizado contra a lista de responsáveis elegíveis (já carregada com o quadro). Quem não estiver lá
     * fica sem foto e cai nas iniciais — degradação silenciosa, como no resto do quadro.
     */
    private carregarFotosDoHistorico(linhas: HistoricoAndamento[]): void {
        const ids = [...new Set(linhas.flatMap((linha) => [this.idDoResponsavelPorNome(linha.usuario), this.idDoResponsavelPorNome(linha.usuarioDestino)]))].filter((id): id is number => id != null);

        this.fotos.carregar(ids);
    }

    /** Id do responsável cujo nome bate com o informado, comparando na forma normalizada. */
    private idDoResponsavelPorNome(nome?: string): number | null {
        const chave = chaveDeNome(nome);
        if (!chave) {
            return null;
        }

        return this.responsaveis().find((responsavel) => chaveDeNome(responsavel.nome) === chave)?.idWebUsuario ?? null;
    }

    /**
     * Classe de cor da coluna a partir do NOME dela, que é o que o histórico registra — `indiceCor`
     * pede o status inteiro, que ali não existe. É o que faz o badge do histórico sair no mesmo tom
     * da coluna no quadro e no diálogo de mover.
     *
     * String vazia para coluna desconhecida (renomeada ou removida do cadastro depois do movimento):
     * sem a classe, `.kanban-badge-status` cai nos fallbacks neutros dela. Chutar um índice pintaria
     * o badge com a cor de OUTRA coluna — pior do que ficar cinza.
     */
    protected classeDeCorDoStatus(nome: string): string {
        const cor = indiceDeCorDoStatus(this.status(), { nome });
        return cor === null ? '' : `kanban-cor-${cor}`;
    }

    /**
     * Foto de quem registrou um evento do histórico, pronta para `src`. `null` quando a pessoa não está
     * entre os responsáveis conhecidos ou não tem foto cadastrada — o template cai nas iniciais.
     */
    protected fotoDoUsuarioDoHistorico(nome: string): string | null {
        const id = this.idDoResponsavelPorNome(nome);
        return id == null ? null : this.fotoResponsavelPorId(id);
    }

    fecharHistorico(): void {
        // Texto herdado apareceria no próximo protocolo aberto, como se fosse dele.
        this.fecharNovoAndamento();
        this.historicoAberto.set(null);
        this.historico.set([]);
        this.carregandoHistorico.set(false);
    }

    /**
     * UF do protocolo, com a do quadro como fallback. A consulta ao portal é por UF: sem isto, item
     * sem `ufCrm` próprio sairia com `ufCrm: ''` — e o quadro sabe a UF, ele carrega por ela. É o
     * mesmo `protocolo.ufCrm ?? obterUfCrm()` que a tela de detalhe faz antes de agir.
     */
    private ufDoProtocolo(item: ItemStatus): string {
        return ((item.ufCrm ?? '').trim() || this.uf().trim()).toUpperCase();
    }

    /**
     * Mesmo gate da tela de detalhe, e fail-closed pelo mesmo motivo: código mapeado sem a role
     * ESCONDE a ação, em vez de mostrá-la inerte — botão desabilitado afirma que a ação existe para
     * quem olha, e o analista vai atrás de um problema que é só de permissão. Código fora do mapa
     * (`roles` indefinido) mantém o default aberto: ausência ali é serviço não catalogado.
     *
     * O primeiro filtro é o mesmo do arraste, e pela mesma razão: `arrastavel` responde "este card
     * tem número/ano", que é a identidade do protocolo no portal. Sem ela não há o que efetivar nem
     * o que cancelar — a chamada sairia com `numeroProtocolo: ''` e o portal responderia sobre outra
     * coisa. Guard reusado de propósito: um segundo teste de identidade sairia de sincronia com este.
     */
    /**
     * QUEM pode efetivar ESTE protocolo: role, serviço, UF e identidade do protocolo. Regra de
     * domínio pura, sem nenhuma noção de onde o card está no quadro — e por isso compartilhável
     * entre o menu do card e o diálogo de mover, que discordam justamente sobre a posição.
     *
     * É a regra de domínio SEM a posição no quadro. Quem soma a posição são os públicos
     * `podeEfetivar`/`podeCancelar`, que acrescentam `noMeioDoFluxo` — regra da tela unitária. O
     * encerramento em lote usa este método (e o de cancelar, abaixo) direto: a posição, ali, é
     * decidida pela coluna dona da seleção, não pelo card individual.
     */
    podeEfetivarProtocolo(item: ItemStatus): boolean {
        const codigo = this.codigoServico();
        if (!codigo || !this.arrastavel(item) || efetivacaoSoNaTelaDeDetalhe(codigo) || acoesOcultasPorUf(codigo, this.ufDoProtocolo(item))) {
            return false;
        }
        const roles = rolesDoServico(codigo);
        return roles ? !!roles.confirmar && this.permissionService.can(roles.confirmar, { uf: this.ufDoProtocolo(item) }) : true;
    }

    /**
     * Idem para o cancelamento — que não tem a exceção da efetivação feita só na tela de detalhe.
     *
     * Mesma observação de `podeEfetivarProtocolo`: é a regra de domínio SEM a posição no quadro; quem
     * soma a posição é o público `podeCancelar`.
     */
    podeCancelarProtocolo(item: ItemStatus): boolean {
        const codigo = this.codigoServico();
        if (!codigo || !this.arrastavel(item) || acoesOcultasPorUf(codigo, this.ufDoProtocolo(item))) {
            return false;
        }
        const roles = rolesDoServico(codigo);
        return roles ? !!roles.cancelar && this.permissionService.can(roles.cancelar, { uf: this.ufDoProtocolo(item) }) : true;
    }

    /**
     * O gate de domínio MAIS a posição do card: no menu do card, e só nele, efetivar e cancelar
     * também dependem de o card estar no meio do fluxo (`noMeioDoFluxo`).
     */
    podeEfetivar(item: ItemStatus): boolean {
        return this.noMeioDoFluxo(item) && this.podeEfetivarProtocolo(item);
    }

    podeCancelar(item: ItemStatus): boolean {
        return this.noMeioDoFluxo(item) && this.podeCancelarProtocolo(item);
    }

    /**
     * Mesma regra da tela de detalhe: protocolo não pago não efetiva — com a mesma exceção.
     *
     * A inscrição de ofício é EXATAMENTE a exceção a este gate, então é aqui que ela entra, e não
     * como um caminho paralelo: quem passa por aqui hoje (o menu do card, o rádio do diálogo, o
     * aviso) passa a enxergá-la de uma vez, sem que nenhum deles precise repetir a pergunta.
     */
    efetivacaoBloqueadaPorPagamento(item: ItemStatus): boolean {
        if (item.inPago === true) {
            return false;
        }

        return !this.seraExOfficio(item);
    }

    /**
     * Efetivar ESTE card vai praticar a inscrição de ofício.
     *
     * Mesma função que decide no card de detalhe e no filtro da coluna. O que este componente
     * acrescenta é só o que a regra pura não tem como saber: o serviço do quadro e a resposta do
     * `PermissionService` para a UF DESTE protocolo (a mesma resolução de `podeEfetivarProtocolo`,
     * não uma segunda leitura de `item.ufCrm` que divergiria em card sem UF própria).
     */
    private seraExOfficio(item: ItemStatus): boolean {
        return efetivacaoSeraExOfficio(this.codigoServico(), item, Date.now(), () => this.permissionService.can(PERMISSOES.pessoaJuridica.prePrestador.exOfficio, { uf: this.ufDoProtocolo(item) }));
    }

    /**
     * O que o analista digitou, sob a chave que o serviço espera. Só entra preenchido: chave vazia no
     * request apagaria, no facade, o valor que a consulta ao portal trouxe.
     */
    private campoDaEfetivacao(codigo: string, valor: string): { dtCancelamento?: string; dtHomologacao?: string; dataDiretoria?: string; observacao?: string } {
        if (!valor) {
            return {};
        }
        if (campoDaConfirmacao(codigo) === 'observacao') {
            return { observacao: valor };
        }
        const chaveData = chaveDaDataDeConfirmacao(codigo);
        return chaveData ? { [chaveData]: dataDeConfirmacaoParaPayload(valor) } : {};
    }

    /**
     * Barra a entrada do card em coluna proibida já no arraste: sem isto o analista arrasta, solta,
     * e só então descobre que não podia, com o card voltando sozinho. `[cdkDropListEnterPredicate]`
     * roda antes do drop, então aqui o card nem chega a entrar na coluna.
     *
     * A guarda equivalente em `onDrop` fica mantida — o predicate é a experiência, e ela é a garantia
     * para quem chamar `onDrop` por outro caminho.
     */
    podeEntrarNaColuna = (drag: CdkDrag, drop: CdkDropList): boolean => {
        const origem = drag.dropContainer.data as KanbanStatus;
        const destino = drop.data as KanbanStatus;
        return origem.id === destino.id || this.destinoPermitido(origem, destino);
    };

    onDrop(evento: CdkDragDrop<KanbanStatus>): void {
        const item = evento.item.data as ItemStatus;
        const origem = evento.previousContainer.data;
        const destino = evento.container.data;

        if (origem.id === destino.id || !this.destinoPermitido(origem, destino)) {
            return;
        }

        this.iniciarMovimento(item, origem, destino);
    }

    iniciarMovimento(item: ItemStatus, origem: KanbanStatus, destino: KanbanStatus): void {
        this.anotacao.set('');
        this.responsavelSelecionado.set(null);
        this.limparEscolhaDaConclusao();
        // Mesmo motivo do histórico: o diálogo mostra o avatar do médico e não depende de o card ter
        // aparecido antes. Consulta já feita não repete.
        this.carregarFotoDoMedico(item);
        // O seletor mostra a foto de cada elegível, e o mapa só tem quem está atribuído em algum
        // card. Busca sob demanda, ao abrir o diálogo, para o custo do LOB não entrar no
        // carregamento do quadro — e só de quem ainda falta.
        if (origem.iniciaAtendimento) {
            this.fotos.carregar(this.responsaveis().map((responsavel) => responsavel.idWebUsuario));
        }
        // Vem preenchida com a criticidade atual do card — mover não é ocasião para redefini-la por
        // omissão. Card ainda não rastreado começa na de menor ordem, que é como o backend o criaria.
        this.criticidadeSelecionada.set(this.solicitacoes().get(chaveProtocolo(item))?.idCriticidade ?? this.criticidadeNormal()?.id ?? null);
        this.movimentoPendente.set({ item, origem, destino });
        // Protocolo já finalizado no portal reabre com a ação registrada marcada — e congelada pelo
        // template. O analista precisa VER o que ficou pendente; o que falta é só a gravação do card.
        const jaFinalizada = this.conclusoesNoPortal.acaoAceita(this.chaveDoAceite(item));
        if (jaFinalizada) {
            this.acaoAoConcluir.set(jaFinalizada);
        }
    }

    /** Busca e abre o e-mail guardado na anotação. */
    abrirEmail(idAnotacao: number): void {
        this.carregandoEmail.set(true);
        this.kanbanServicosService
            .obterEmailDaAnotacao(idAnotacao)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (html) => {
                    this.carregandoEmail.set(false);
                    this.emailAberto.set(html);
                },
                error: (falha: unknown) => {
                    this.carregandoEmail.set(false);
                    this.logger.error('Falha ao buscar o e-mail da anotação:', falha);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível carregar o e-mail enviado.'
                    });
                }
            });
    }

    fecharEmail(): void {
        this.emailAberto.set(null);
    }

    /** A coluna tem filtro aplicado — marca o ícone e libera o "Limpar". */
    colunaFiltrada(coluna: KanbanStatus): boolean {
        return filtroAtivo(this.filtrosPorColuna().get(coluna.id));
    }

    abrirFiltro(coluna: KanbanStatus): void {
        this.rascunhoFiltro.set({ ...(this.filtrosPorColuna().get(coluna.id) ?? {}) });
        this.colunaEmFiltro.set(coluna);
    }

    fecharFiltro(): void {
        this.colunaEmFiltro.set(null);
    }

    /** Escreve um campo do rascunho. Valor vazio REMOVE o campo, senão `filtroAtivo` o veria preenchido. */
    definirNoRascunho(campo: keyof FiltroDaColuna, valor: unknown): void {
        this.rascunhoFiltro.update((atual) => {
            const proximo = { ...atual };
            if (valor === null || valor === undefined || valor === '') {
                delete proximo[campo];
            } else {
                Object.assign(proximo, { [campo]: valor });
            }
            return proximo;
        });
    }

    aplicarFiltro(): void {
        const coluna = this.colunaEmFiltro();
        if (!coluna) {
            return;
        }
        // Card filtrado para fora não pode continuar na fila do lote, invisível ao analista.
        this.limparSelecionados();
        const rascunho = this.rascunhoFiltro();
        this.filtrosPorColuna.update((mapa) => {
            const proximo = new Map(mapa);
            // Filtro sem nenhum campo sai do mapa em vez de ficar como objeto vazio: assim
            // `colunaFiltrada` responde pela ausência, e não por uma varredura de campos.
            if (filtroAtivo(rascunho)) {
                proximo.set(coluna.id, rascunho);
            } else {
                proximo.delete(coluna.id);
            }
            return proximo;
        });
        this.fecharFiltro();
    }

    limparFiltro(): void {
        this.rascunhoFiltro.set({});
        const coluna = this.colunaEmFiltro();
        if (coluna) {
            this.filtrosPorColuna.update((mapa) => {
                const proximo = new Map(mapa);
                proximo.delete(coluna.id);
                return proximo;
            });
        }
        this.fecharFiltro();
    }

    cancelarMovimento(): void {
        this.movimentoPendente.set(null);
        this.anotacao.set('');
        // Zerado junto com o resto: marcacao herdada do movimento anterior dispararia um e-mail que
        // ninguem pediu no movimento seguinte.
        this.avisarMedico.set(false);
        this.responsavelSelecionado.set(null);
        this.criticidadeSelecionada.set(null);
        this.limparEscolhaDaConclusao();
    }

    /**
     * Escolha da conclusão e os dois campos que ela pode exigir. Zerada ao abrir e ao fechar o
     * diálogo: escolha herdada do movimento anterior sairia despercebida.
     *
     * `salvando` entra junto porque ele desabilita Confirmar e Cancelar: preso em `true` por um
     * caminho que retornou sem desligá-lo, ele travaria TODOS os movimentos seguintes até recarregar
     * a página. Abrir ou fechar o diálogo é onde não há nada em voo, e portanto onde ele pode voltar
     * ao chão com segurança.
     *
     * O que NÃO entra aqui é a memória de aceites do portal (`ConclusoesNoPortalService`): aquilo não é escolha de diálogo, é o
     * registro de um fato irreversível do portal, e tem de sobreviver a abrir e fechar. Ele sai
     * quando o movimento correspondente é finalmente gravado.
     */
    private limparEscolhaDaConclusao(): void {
        this.acaoAoConcluir.set(null);
        this.motivoDaConclusao.set('');
        this.dadoDaEfetivacao.set('');
        this.salvando.set(false);
    }

    /**
     * Confirma o movimento pendente. Para coluna conclusiva o PORTAL DECIDE PRIMEIRO: o card só entra
     * na coluna depois que a solicitação foi mesmo finalizada lá.
     *
     * A ordem é o ponto: gravar o movimento antes deixaria, numa recusa do portal, um card em
     * CONCLUÍDO com o protocolo em aberto — e o quadro passaria a mentir sobre o estado do protocolo,
     * que é a única coisa que ele existe para mostrar. O caminho inverso (portal aceitou, gravação
     * falhou) é recuperável: repetir o movimento refaz só o `mover`, sem tocar no portal de novo
     * (`ConclusoesNoPortalService`).
     *
     * <p>Onda de correção final (Crítico 2): guarda de `somenteLeitura()` explícita aqui, na frente de
     * `finalizarSolicitacao` — o ato irreversível no portal, que corre ANTES de `gravarMovimento` (que
     * já checava). Registro honesto: tentei produzir um RED de verdade para este ponto e não consegui
     * — hoje `podeConfirmar()` já barra o ramo de `exigeEscolhaAoConcluir()` por uma via indireta
     * (`arrastavel` devolve `false` no modo leitura, e isso cascateia por `podeEfetivarProtocolo`/
     * `podeCancelarProtocolo` até `escolhaDaConclusaoCompleta`, fazendo `podeConfirmar()` falso). Essa
     * proteção é ACIDENTAL: nasceu para desabilitar o arraste, não para isto, e nada a documenta como
     * defesa do modo leitura — um refactor futuro de qualquer elo dessa cadeia reabriria a porta sem
     * que ninguém percebesse. Esta linha existe para não depender dela.</p>
     */
    confirmarMovimento(): void {
        if (this.ufEfetivaStore.somenteLeitura()) {
            return;
        }

        const pendente = this.movimentoPendente();

        // O portal já finalizou ESTE protocolo: repetir a chamada seria uma segunda finalização sobre
        // o que já foi finalizado. Quem vale aqui é a ação REGISTRADA, nunca a marcada na tela — o
        // rádio está congelado justamente porque trocá-lo não desfaz o que o portal executou.
        const jaFinalizada = this.exigeEscolhaAoConcluir() ? this.acaoJaFinalizadaNoPortal() : null;
        if (jaFinalizada) {
            if (!pendente || !this.podeConfirmar()) {
                return;
            }
            this.gravarMovimento(jaFinalizada);
            return;
        }

        const acao = this.exigeEscolhaAoConcluir() ? this.acaoAoConcluir() : null;

        if (!acao) {
            this.gravarMovimento();
            return;
        }

        // Repetido de `gravarMovimento` porque aqui ele decide se a chamada ao portal chega a sair —
        // e uma chamada ao portal é irreversível, diferente de um movimento no quadro.
        if (!pendente || !this.podeConfirmar()) {
            return;
        }

        this.salvando.set(true);
        this.facade
            // O motivo viaja por parâmetro próprio; data e observação viajam DENTRO do request, e só
            // quando a ação é efetivar — são campos da confirmação, não do cancelamento.
            .finalizarSolicitacao(this.requestDaConclusao(pendente.item, acao), acao, acao === 'cancelar' ? this.motivoDaConclusao().trim() : undefined)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => this.aoPortalAceitar(pendente, acao),
                error: (falha: unknown) => {
                    this.salvando.set(false);
                    this.logger.error('Falha ao finalizar a solicitação no portal:', falha);
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'A solicitação não foi finalizada no portal. O card permanece onde está.'
                    });
                }
            });
    }

    /**
     * O portal aceitou: agora sim o movimento é gravado.
     *
     * O `pendente` capturado na ida é comparado com o de agora porque a chamada é assíncrona e o
     * diálogo pode ter sido fechado (ou trocado por outro movimento) no meio dela. Não fosse a
     * comparação, `gravarMovimento` sairia mudo no primeiro guard — a solicitação finalizada no
     * portal e o quadro sem nada, sem ninguém saber — ou, pior, gravaria o movimento do OUTRO card.
     */
    private aoPortalAceitar(pendente: MovimentoPendente, acao: AcaoServico): void {
        // ANTES de qualquer desvio: o portal executou, e isso não se desfaz. Registrar aqui vale
        // também para o ramo em que o diálogo já sumiu — é o que impede que um novo arraste do mesmo
        // card finalize uma segunda vez.
        this.conclusoesNoPortal.registrar(this.chaveDoAceite(pendente.item), acao);

        if (this.movimentoPendente() !== pendente) {
            this.salvando.set(false);
            this.logger.warn('Solicitação finalizada no portal sem o movimento correspondente no quadro:', pendente.item.portal);
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                // NUNCA "mova-o novamente": neste ramo o card continua na tela, e mover de novo
                // reentraria em `finalizarSolicitacao` — uma segunda finalização sobre um protocolo
                // já finalizado, cujo erro apareceria como "não foi finalizada no portal", que é falso.
                detail: 'A solicitação foi finalizada no portal, mas o card não chegou a ser movido. Recarregue o quadro ou ajuste pela tela do protocolo.'
            });
            return;
        }

        this.gravarMovimento(acao);
    }

    /**
     * Identidade do protocolo no portal mais, na efetivação, o campo que o serviço exige e QUAL
     * efetivação é: a comum ou a de ofício.
     *
     * `exOfficio` viaja daqui até o desvio único do facade — o mesmo por onde passa o botão da tela
     * de detalhe. É o que faz o arraste praticar exatamente o ato que o rádio nomeou e o aviso
     * descreveu, em vez de reproduzir a decisão outra vez lá dentro.
     */
    private requestDaConclusao(item: ItemStatus, acao: AcaoServico): { numeroProtocolo: string; anoProtocolo: string; ufCrm: string; codigoServico: string; exOfficio?: boolean } {
        const codigo = this.codigoServico();
        const request = {
            numeroProtocolo: item.numeroProtocolo ?? '',
            anoProtocolo: item.anoProtocolo ?? '',
            ufCrm: this.ufDoProtocolo(item),
            codigoServico: codigo
        };

        return acao === 'efetivar' ? { ...request, ...this.campoDaEfetivacao(codigo, this.dadoDaEfetivacao().trim()), exOfficio: this.seraExOfficio(item) } : request;
    }

    /**
     * Grava o movimento no quadro. Sem otimista/rollback: o card só troca de coluna no sucesso.
     *
     * `acaoNoPortal` é o que o portal JÁ aceitou antes desta gravação, ou `null` num movimento que
     * não passou pelo portal. É o que decide o desfecho: só com o movimento gravado é que a
     * finalização pode ser anunciada como sucesso e a lista do serviço recarregada. Anunciar antes —
     * como se fazia, na mesma tick da chamada — terminava, numa falha do `mover`, com a solicitação
     * finalizada no portal, a linha do quadro nunca movida, dois toasts contraditórios e o card
     * sumido do quadro, porque a recarga já o havia tirado da lista.
     *
     * A checagem de `somenteLeitura()` fica AQUI, e não em `onDrop`/`destinoPermitido`: aquelas
     * guardas existem para a EXPERIÊNCIA (a tela não oferece o que o perfil nacional não pode fazer),
     * mas não se defendem sozinhas.
     *
     * <p>Onda de correção final (Correção 5 — reescrita de um comentário falso): esta função NÃO é o
     * gargalo do módulo. O comentário anterior afirmava que "os cinco caminhos de escrita do quadro
     * convergem para `gravarMovimento`" e que `.mover(` teria um único ponto de chamada — falso, e uma
     * completude falsa é pior que nenhuma, porque diz ao próximo mantenedor que não precisa verificar.
     * Verificado com grep em `.mover(` e `finalizarSolicitacao(` no módulo `features/servicos`
     * inteiro: cada um tem DOIS pontos de chamada, não um. `.mover(` — aqui (`gravarMovimento`, guarda
     * acima) e em `EncerramentoLoteService.moverEFinalizar` (guarda própria). `finalizarSolicitacao(`
     * — em `confirmarMovimento`, alguns métodos acima (guarda própria, porque é o ato IRREVERSÍVEL no
     * portal e roda ANTES desta gravação) e em `EncerramentoLoteService.executarDoZero` (guarda
     * própria). Ou seja: cada arquivo, e dentro dele cada função que decide uma escrita, guarda a sua
     * própria porta — a guarda daqui vale só para as chamadas a `.mover(` que passam por ESTA função,
     * nunca por tabela. Quem adicionar uma via de escrita nova precisa re-verificar com grep na hora,
     * não herdar esta lista: ela descreve o estado no momento em que foi escrita, não uma garantia
     * permanente.</p>
     */
    private gravarMovimento(acaoNoPortal: AcaoServico | null = null): void {
        if (this.ufEfetivaStore.somenteLeitura()) {
            return;
        }

        const pendente = this.movimentoPendente();
        const idServico = this.idServico();
        const uf = this.uf();

        if (!pendente || idServico == null || !uf || !this.podeConfirmar()) {
            return;
        }

        const numero = inteiro(pendente.item.numeroProtocolo);
        const ano = inteiro(pendente.item.anoProtocolo);

        if (numero === null || ano === null) {
            this.cancelarMovimento();
            return;
        }

        const anotacao = this.anotacao().trim();
        this.salvando.set(true);

        this.kanbanServicosService
            .mover({
                idServico,
                numero,
                ano,
                uf,
                idStatusDestino: pendente.destino.id,
                anotacao: anotacao || undefined,
                idWebUsuarioResponsavel: this.responsavelSelecionado() ?? undefined,
                idCriticidade: this.criticidadeSelecionada() ?? undefined,
                // O que o portal ACABOU de executar, e não o que está marcado na tela: nos ramos em
                // que a ação vem congelada (`acaoJaFinalizadaNoPortal`), o rádio já não decide nada.
                // É a única passagem em que este desfecho existe — não há de onde apurá-lo depois.
                desfecho: acaoNoPortal ? desfechoDaAcao(acaoNoPortal) : undefined
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (resposta) => {
                    // O protocolo se moveu pelo fluxo UNITÁRIO: se estava marcado para o lote, a
                    // marca não pode continuar apontando para a coluna de onde ele acabou de sair —
                    // remove só ELE, e não a seleção inteira, que os demais continuam válidos.
                    this.removerDaSelecao(chaveProtocolo(pendente.item));
                    this.solicitacoes.update((mapa) => {
                        const chave = chaveNumeroAno(ano, numero);
                        const anterior = mapa.get(chave);
                        const atualizado = new Map(mapa);
                        atualizado.set(chave, {
                            idSolicitacao: resposta.idSolicitacao,
                            numero,
                            ano,
                            uf,
                            idStatus: resposta.idStatus,
                            dtAbertura: anterior?.dtAbertura,
                            dtConclusao: resposta.dtConclusao,
                            dtUltimaMovimentacao: resposta.dtUltimaMovimentacao,
                            totalAnotacoes: (anterior?.totalAnotacoes ?? 0) + (anotacao ? 1 : 0),
                            idCriticidade: resposta.idCriticidade,
                            idWebUsuarioResponsavel: resposta.idWebUsuarioResponsavel,
                            nomeResponsavel: this.nomeDoResponsavel(resposta.idWebUsuarioResponsavel)
                        });
                        return atualizado;
                    });
                    // Réplica da condição de `KanbanServicosService.mover` no backend: só quando a
                    // ORIGEM inicia atendimento é que o portal tramita A -> T. `mover` não devolve a
                    // situação do portal, então o 'T' é inferido daqui — marcar em qualquer sucesso
                    // mentiria nos demais movimentos, que não tramitam nada.
                    //
                    // E nunca para destino conclusivo: ali a solicitação acabou de ser efetivada ou
                    // cancelada, e marcar 'T' (tramitado) mentiria sobre a situação dela no portal.
                    if (pendente.origem.iniciaAtendimento && !pendente.destino.conclusivo) {
                        this.facade.atualizarSituacaoProtocolo(pendente.item.numeroProtocolo ?? '', pendente.item.anoProtocolo ?? '', this.ufDoProtocolo(pendente.item), 'T');
                    }
                    this.salvando.set(false);

                    // Destino conclusivo: o card acabou de deixar de existir na listagem viva, e é a
                    // consulta dos concluídos que passa a respondê-lo. Sem esta recarga o protocolo
                    // ficaria concluído e FORA do quadro até o próximo F5 — que é o defeito que a
                    // coluna Concluído existe para fechar.
                    if (pendente.destino.conclusivo) {
                        this.carregarConcluidos(idServico, uf, () => this.idServico() === idServico && this.uf() === uf);
                    }

                    // Só AQUI, com o movimento gravado, é que a finalização vira sucesso na tela e a
                    // lista do serviço recarrega — o protocolo finalizado sai dela, e sem a recarga o
                    // card continuaria no quadro convidando a uma segunda finalização.
                    if (acaoNoPortal) {
                        // A pendência acabou: portal finalizado E movimento gravado. A memória do
                        // aceite sai daqui, e só daqui — antes disso ela é a garantia contra a
                        // segunda finalização.
                        this.conclusoesNoPortal.esquecer(this.chaveDoAceite(pendente.item));
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Sucesso',
                            detail: acaoNoPortal === 'efetivar' ? 'Solicitação efetivada com sucesso.' : 'Solicitação cancelada com sucesso.'
                        });
                        this.facade.carregarProtocolosPorServico(this.codigoServico(), [this.ufDoProtocolo(pendente.item)]);
                    }

                    // Depois da gravacao, e nao antes: o aviso avisa sobre uma pendencia que ja EXISTE
                    // no quadro. Pedir antes deixaria o medico avisado de uma pendencia que o `mover`
                    // ainda podia recusar.
                    const avisarMedico = this.avisarMedico() && pendente.destino.avisaPendencia;
                    this.cancelarMovimento();
                    if (avisarMedico) {
                        this.avisoPendenciaSolicitado.emit(pendente.item);
                    }
                },
                error: (falha: { error?: { title?: string } }) => {
                    this.salvando.set(false);

                    // Portal já finalizou e a gravação falhou: o diálogo FICA ABERTO, com a escolha e
                    // o texto digitado intactos — é a única via de recuperação, e `cancelarMovimento`
                    // a apagaria. Repetir o Confirmar daqui refaz só o `mover`.
                    if (acaoNoPortal) {
                        this.logger.error('Solicitação finalizada no portal, mas o movimento não foi gravado:', falha);
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Atenção',
                            detail: `A solicitação foi ${acaoNoPortal === 'efetivar' ? 'efetivada' : 'cancelada'} no portal, mas o card não foi movido: ${falha?.error?.title ?? 'não foi possível mover a solicitação.'} Confirme novamente para mover o card — a solicitação não será finalizada outra vez.`
                        });
                        return;
                    }

                    this.cancelarMovimento();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: falha?.error?.title ?? 'Não foi possível mover a solicitação.'
                    });
                }
            });
    }

    onSelect(item: ItemStatus): void {
        this.itemSelected.emit(item);
    }
}
